
import { CanvasNode, Connection, ViewportTransform, HistoryItem, LightHistoryItem, NodeData, UserProfile, LocalUserCredentials, FirebaseConfig, NodeType, UserSubscription, RecentFileMetadata, ProjectSchemaVersion } from '../types';
import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, User, signOut, updateProfile, onAuthStateChanged, deleteUser, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, getDocFromServer, updateDoc, collection, query, where, getDocs, getDocsFromServer, deleteDoc, onSnapshot, writeBatch, DocumentChange, increment, runTransaction, deleteField, type QuerySnapshot } from 'firebase/firestore';
import { getStorage, ref, uploadString, getDownloadURL, listAll, getMetadata, deleteObject } from 'firebase/storage';
import { firebaseConfig } from '../firebaseConfig';
import { registerFile, createFileReference, isFileReference, FILE_REF_PREFIX, uploadWithDeduplication, isBlobUploading, finishBlobUpload, resolveUploadedBlob, markRehydratedBlob, StorageCategory as FileRegistryCategory, type FileMetadata } from './fileRegistry';
import { historyItemFields, historyTotalCost } from './historyItems';
import { parseReferenceStorageName, videoExtensionFromMime, type ReferenceStorageCategory } from './referenceMedia';
import { AuthRequiredError, ValidationError } from './errors';
import { logger } from './logger';
import { audioExtensionFromMime } from './audioReference';
import { isLocalMediaUrl } from './desktopFolder';
import { noteWorkspaceSaved } from './workspaceSaves';

const DB_NAME = 'InfiniteCanvasDB';
// The database is opened without a version (see openDB); stores are added on demand.
// Historical versions: 6 = recent files store, 7 = folders store.
const STORE_NAME = 'projects';
const USER_STORE_NAME = 'users';
const LOCAL_WORKSPACES_STORE = 'localWorkspaces'; // For offline/free user workspaces
const RECENT_FILES_STORE = 'recentFiles'; // For recent files metadata
/** Project / folder containers that organise workspaces (mirrors the Firestore `folders` collection). */
export const FOLDERS_STORE = 'folders';

// --- STORAGE CATEGORY TYPES ---
export type StorageCategory = 
    | 'Generated_Images'
    | 'Uploaded_Images'
    | 'Videos'
    | 'Audio'
    | 'Sketches'
    | 'Characters'
    | 'Sets'
    | 'Scripts';

// Helper to sanitize workspace name for use in storage paths
const sanitizeWorkspaceName = (name: string): string => {
    return name.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50) || 'Untitled';
};

// Helper to generate storage path with new folder structure
const getStoragePath = (
    userId: string, 
    workspaceName: string, 
    category: StorageCategory, 
    timestamp: number, 
    extension: string
): string => {
    const safeName = sanitizeWorkspaceName(workspaceName);
    const prefix = category.toLowerCase().replace('_', '-');
    return `users/${userId}/${safeName}/${category}/${prefix}_${timestamp}.${extension}`;
};

// Map node type to storage category
const getStorageCategory = (nodeType: NodeType, dataField: 'video' | 'generated' | 'uploaded' | 'sketch' | 'reference'): StorageCategory => {
    if (dataField === 'video') return 'Videos';
    if (dataField === 'generated') return 'Generated_Images';
    if (dataField === 'sketch') return 'Sketches';
    
    // For uploaded/reference images, check node type
    switch (nodeType) {
        case NodeType.CHARACTER_NODE:
            return 'Characters';
        case NodeType.LOCATION_NODE:
        case NodeType.PROP_NODE:
            return 'Sets';
        case NodeType.SKETCH_PAD:
            return 'Sketches';
        default:
            return 'Uploaded_Images';
    }
};

export interface ProjectMetadata {
    id: string;
    name: string;
    ownerId: string;
    /** The owner's name, as their saves write it; absent until they save once with this version */
    ownerName?: string;
    lastSaved: number;
    createdAt?: number;
    nodeCount?: number;
    thumbnail?: string;
    isLocal?: boolean;
    isShared?: boolean;
    sharedPermission?: 'view' | 'edit';
    /** The name of who shared it with this person (the owner's when that is all that is known) */
    sharedBy?: string;
    /** Shared with this person as part of a library project of the owner's (the project's id): it is shown inside that project */
    sharedProjectId?: string;
    /** Container (project or folder) this workspace is filed under; null/undefined = unfiled. */
    folderId?: string | null;
    isFavorite?: boolean;
    /** What its generations cost, in US dollars: its History's total (utils/historyItems.ts) */
    totalCost?: number;
}

// --- FIREBASE STATE ---
let firebaseApp: FirebaseApp | null = null;
let activeConfig: FirebaseConfig | null = null;

// Initialize Firebase automatically
export const initFirebase = () => {
    console.log('[INIT] Firebase initialization starting...');
    console.log('[INIT] firebaseConfig:', {
        apiKey: firebaseConfig.apiKey ? firebaseConfig.apiKey.substring(0, 5) + '...' : 'MISSING',
        authDomain: firebaseConfig.authDomain,
        projectId: firebaseConfig.projectId,
        appId: firebaseConfig.appId ? firebaseConfig.appId.substring(0, 10) + '...' : 'MISSING'
    });
    
    if (firebaseConfig.apiKey === "YOUR_API_KEY_HERE") {
        console.warn("[INIT] Firebase Config is using placeholders. Cloud features disabled.");
        return false;
    }

    try {
        const existingApps = getApps();
        console.log('[INIT] Existing Firebase apps:', existingApps.length);
        
        if (!existingApps.length) {
            console.log('[INIT] Initializing new Firebase app...');
            firebaseApp = initializeApp(firebaseConfig);
            console.log('[INIT] Firebase app initialized successfully:', firebaseApp.name);
        } else {
            firebaseApp = existingApps[0];
            console.log('[INIT] Using existing Firebase app:', firebaseApp.name);
        }
        activeConfig = firebaseConfig;
        console.log('[INIT] Firebase initialization complete. isFirebaseActive:', !!firebaseApp);
        return true;
    } catch (e) {
        console.error("[INIT] Firebase init failed:", e);
        console.warn("[INIT] Falling back to local mode due to:", e instanceof Error ? e.message : String(e));
        return false;
    }
};

export const isFirebaseActive = () => !!firebaseApp;
export const getFirebaseApp = () => firebaseApp;

/** The signed-in person's name as the app shows it (their address when they gave none); null when signed out. */
export const currentUserName = (): string | null => {
    const user = firebaseApp ? getAuth(firebaseApp).currentUser : null;
    return user ? (user.displayName || user.email || null) : null;
};

/** The signed-in user's ID token, for a direct Storage download that does not go through the SDK; null when signed out. */
export const getCurrentIdToken = async (): Promise<string | null> => {
    if (!firebaseApp) return null;
    const user = getAuth(firebaseApp).currentUser;
    return user ? user.getIdToken() : null;
};

// --- FIRESTORE WRITE AVAILABILITY ---
// Tracks whether Firestore writes are permitted. Starts as `null` (unknown).
// On the first Firestore SDK write attempt:
//   - success → cached as `true`
//   - permission-denied/403 → cached as `false`, all future writes silently skip
// This avoids upfront REST probes (which Chrome logs as 404/403 in the console).
let _firestoreWriteOk: boolean | null = null;

/** Check cached Firestore write availability. null = not tested yet. */
export const isFirestoreWriteAvailable = (): boolean | null => _firestoreWriteOk;

/** Mark Firestore writes as unavailable (called on first permission-denied). */
export const markFirestoreWriteDenied = () => {
    if (_firestoreWriteOk !== false) {
        _firestoreWriteOk = false;
        logger.warn('Storage', 'Firestore writes denied (permission-denied) — disabled for this session');
    }
};

/** Mark Firestore writes as available (called on first successful write). */
export const markFirestoreWriteOk = () => { _firestoreWriteOk = true; };

/** Reset write availability on logout so next login re-tests. */
export const resetFirestoreProbe = () => { _firestoreWriteOk = null; };

// --- SESSION ID FOR SYNC ECHO SUPPRESSION ---
// Unique per-tab session ID used to tag writes and skip self-originated onSnapshot updates
let _syncSessionId: string | null = null;
export const getSyncSessionId = (): string => {
    if (!_syncSessionId) {
        try {
            _syncSessionId = `${window.location.origin}_${crypto.randomUUID()}`;
        } catch {
            _syncSessionId = `${window.location.origin}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        }
    }
    return _syncSessionId;
};

// --- AUTH VALIDATION HELPERS ---

/**
 * Validates that a userId is provided
 * @throws AuthRequiredError if userId is missing
 */
export function requireAuth(userId: string | null | undefined, operation: string): asserts userId is string {
    if (!userId) {
        logger.warn('Storage', `Auth required for operation: ${operation}`);
        throw new AuthRequiredError(`Authentication required for ${operation}`);
    }
}

/**
 * Validates that required parameters are provided
 * @throws ValidationError if validation fails
 */
export const validateParams = (
    params: Record<string, any>,
    operation: string
): void => {
    const missing = Object.entries(params)
        .filter(([_, value]) => value === null || value === undefined || value === '')
        .map(([key]) => key);
    
    if (missing.length > 0) {
        logger.warn('Storage', `Missing parameters for ${operation}: ${missing.join(', ')}`);
        throw new ValidationError(`Missing required parameters: ${missing.join(', ')}`);
    }
};

export const subscribeToAuthChanges = (callback: (user: UserProfile | null) => void) => {
    if (isFirebaseActive()) {
        const auth = getAuth(firebaseApp!);
        logger.debug('Storage', 'Setting up Firebase auth state listener...');
        
        // Ensure session persistence across page reloads
        setPersistence(auth, browserLocalPersistence).catch(e => {
            console.warn('Could not set auth persistence:', e);
        });
        
        return onAuthStateChanged(auth, async (user) => {
            logger.debug('Storage', `Auth state changed: ${user ? 'signed in as ' + user.uid : 'signed out'}`);
            if (user) {
                // Fetch role from Firestore user document (admin/superadmin removed)
                let role: 'user' | 'admin' | 'superadmin' = 'user';
                const permissions: string[] = [];
                let walletAddress: string | undefined;
                let docDisplayName: string | undefined;
                try {
                    await user.getIdToken();
                    const db = getFirestore(firebaseApp!);
                    const userDocRef = doc(db, 'users', user.uid);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        if (data?.role) {
                            role = (data.role === 'admin' || data.role === 'superadmin') ? 'user' : data.role;
                        }
                        walletAddress = data?.walletAddress;
                        docDisplayName = data?.displayName;
                    }
                } catch (e) {
                    console.warn('[AUTH] Could not fetch user doc:', e);
                }
                // Wallet users have no email — derive a short label from the address
                const shortWallet = walletAddress ? `${walletAddress.slice(0, 4)}…${walletAddress.slice(-4)}` : undefined;
                callback({
                    name: user.displayName || docDisplayName || user.email?.split('@')[0] || shortWallet || 'User',
                    email: user.email || '',
                    picture: user.photoURL || '',
                    sub: user.uid,
                    role,
                    permissions,
                    walletAddress,
                });
            } else {
                callback(null);
            }
        });
    }
    // Firebase not active - immediately call back with null user
    logger.warn('Storage', 'Firebase not active, auth subscription returning null');
    callback(null);
    return () => {};
};


// --- INDEXED DB (LOCAL) HELPERS ---
/** Every object store the app expects; missing ones are created on upgrade. */
const REQUIRED_STORES = [STORE_NAME, USER_STORE_NAME, LOCAL_WORKSPACES_STORE, RECENT_FILES_STORE, FOLDERS_STORE];

const ensureStores = (db: IDBDatabase) => {
  if (!db.objectStoreNames.contains(STORE_NAME)) {
      // We use out-of-line keys (projectId)
      db.createObjectStore(STORE_NAME);
  }
  if (!db.objectStoreNames.contains(USER_STORE_NAME)) {
      db.createObjectStore(USER_STORE_NAME, { keyPath: 'email' });
  }
  // Local workspaces store for offline/free users (no userId required)
  if (!db.objectStoreNames.contains(LOCAL_WORKSPACES_STORE)) {
      db.createObjectStore(LOCAL_WORKSPACES_STORE, { keyPath: 'id' });
      console.log('Created localWorkspaces store');
  }
  // Recent files metadata store
  if (!db.objectStoreNames.contains(RECENT_FILES_STORE)) {
      db.createObjectStore(RECENT_FILES_STORE, { keyPath: 'id' });
      console.log('Created recentFiles store');
  }
  // Projects / folders that organise workspaces
  if (!db.objectStoreNames.contains(FOLDERS_STORE)) {
      db.createObjectStore(FOLDERS_STORE, { keyPath: 'id' });
      console.log('Created folders store');
  }
};

export const DB_BLOCKED_MESSAGE = 'Local storage is being updated but another tab of this app is holding it open. Close the other tabs and reload.';
const DB_BLOCKED_GRACE_MS = 4000;

/** Older tabs must let go of the database when a newer tab upgrades it. */
const releaseOnVersionChange = (db: IDBDatabase): IDBDatabase => {
  db.onversionchange = () => db.close();
  return db;
};

/**
 * Open the app database and make sure every store exists.
 *
 * The database is opened WITHOUT a version first: opening with a fixed number
 * throws VersionError as soon as the on-disk version is higher (which happens
 * after any forced upgrade below), and that used to take every read and write
 * down with it. Missing stores are then added by re-opening at version + 1.
 * `blocked` (another tab still holding an old connection) gets a short grace
 * period — tabs running this code close themselves on `versionchange` — before
 * failing with a message the UI can show.
 */
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = (fn: () => void) => { if (!settled) { settled = true; fn(); } };

    const attachBlockedGuard = (req: IDBOpenDBRequest) => {
      req.onblocked = () => {
        console.warn('[openDB] Upgrade blocked by another connection; waiting for it to close…');
        setTimeout(() => done(() => reject(new Error(DB_BLOCKED_MESSAGE))), DB_BLOCKED_GRACE_MS);
      };
    };

    const request = indexedDB.open(DB_NAME);
    request.onerror = () => done(() => reject(request.error));
    attachBlockedGuard(request);
    request.onupgradeneeded = (event) => {
      // Brand-new database: create everything at version 1
      ensureStores((event.target as IDBOpenDBRequest).result);
    };
    request.onsuccess = () => {
      const db = request.result;
      const missing = REQUIRED_STORES.filter(s => !db.objectStoreNames.contains(s));
      if (missing.length === 0) {
        done(() => resolve(releaseOnVersionChange(db)));
        return;
      }
      console.log(`Missing IndexedDB stores (${missing.join(', ')}), upgrading from version ${db.version}...`);
      const nextVersion = db.version + 1;
      db.close();
      const upgradeRequest = indexedDB.open(DB_NAME, nextVersion);
      upgradeRequest.onerror = () => done(() => reject(upgradeRequest.error));
      attachBlockedGuard(upgradeRequest);
      upgradeRequest.onupgradeneeded = (event) => {
        ensureStores((event.target as IDBOpenDBRequest).result);
      };
      upgradeRequest.onsuccess = () => done(() => resolve(releaseOnVersionChange(upgradeRequest.result)));
    };
  });
};

/** Exposed for sibling storage modules (e.g. workspaceFolders.ts) that share the same IndexedDB. */
export const openLocalDB = openDB;

const hashPassword = async (password: string): Promise<string> => {
  const msgBuffer = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};


// --- AUTHENTICATION ---

export const registerUser = async (name: string, email: string, password: string): Promise<UserProfile> => {
    console.log('[REGISTER] Starting registration...');
    console.log('[REGISTER] isFirebaseActive:', isFirebaseActive());
    console.log('[REGISTER] firebaseApp:', firebaseApp);
    
    if (!isFirebaseActive()) {
        console.error('[REGISTER] Firebase is NOT active. Account creation blocked.');
        throw new Error('Account creation requires an internet connection. Please check your connection and try again.');
    }
    
    console.log('[REGISTER] Firebase is active. Checking for existing email:', email);
    
    // Check if email exists in Firestore users collection (for legacy users)
    try {
        const db = getFirestore(firebaseApp!);
        const usersQuery = query(collection(db, 'users'), where('email', '==', email));
        const existingUsers = await getDocs(usersQuery);
        
        if (!existingUsers.empty) {
            console.error('[REGISTER] Email already exists in database:', email);
            throw new Error('auth/email-already-in-use');
        }
    } catch (e: any) {
        // If it's our email-already-in-use error, re-throw it
        if (e.message === 'auth/email-already-in-use') {
            throw new Error('The email address is already in use by another account.');
        }
        // Otherwise log and continue (permission errors, etc.)
        console.warn('[REGISTER] Could not check Firestore for existing email:', e);
    }
    
    console.log('[REGISTER] Creating Firebase Auth account for:', email);
    const auth = getAuth(firebaseApp!);
    
    try {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name });
        
        // Store user email in Firestore for future duplicate checks
        const db = getFirestore(firebaseApp!);
        await setDoc(doc(db, 'users', cred.user.uid), {
            email: email,
            name: name,
            createdAt: Date.now(),
            updatedAt: Date.now()
        }, { merge: true });
        
        console.log('[REGISTER] Account created successfully:', cred.user.uid);
        
        // Generate verification link via our Netlify function (bypasses Firebase's unreliable email sender)
        let verificationLink: string | undefined;
        try {
            const resp = await fetch('/.netlify/functions/send-verification-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await resp.json();
            if (resp.ok && data.verificationLink) {
                verificationLink = data.verificationLink;
                console.log('[REGISTER] Got verification link');
            }
        } catch (verifyErr) {
            console.warn('[REGISTER] Could not generate verification link:', verifyErr);
        }
        
        return {
            name: name,
            email: email,
            picture: '',
            sub: cred.user.uid,
            verificationLink,
        };
    } catch (e: any) {
        console.error('[REGISTER] Firebase error:', e.code, e.message);
        if (e.code === 'auth/email-already-in-use') {
            throw new Error('The email address is already in use by another account.');
        }
        throw e;
    }
};

export const loginUser = async (email: string, password: string): Promise<UserProfile> => {
    console.log('[LOGIN] Starting login...');
    console.log('[LOGIN] isFirebaseActive:', isFirebaseActive());
    console.log('[LOGIN] firebaseApp:', firebaseApp);
    
    if (!isFirebaseActive()) {
        console.error('[LOGIN] Firebase is NOT active');
        throw new Error('Sign in requires an internet connection. Please check your connection and try again.');
    }
    
    console.log('[LOGIN] Firebase is active, attempting signIn for:', email);
    const auth = getAuth(firebaseApp!);
    try {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        console.log('[LOGIN] Login successful:', cred.user.uid);
        // Fetch role from Firestore (admin/superadmin removed)
        let role: 'user' | 'admin' | 'superadmin' = 'user';
        const permissions: string[] = [];
        try {
            const db = getFirestore(firebaseApp!);
            const userDocRef = doc(db, 'users', cred.user.uid);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists() && userDoc.data()?.role) {
                const r = userDoc.data().role;
                role = (r === 'admin' || r === 'superadmin') ? 'user' : r;
            }
        } catch (e) {
            console.warn('[LOGIN] Could not fetch user role:', e);
        }
        return {
            name: cred.user.displayName || email.split('@')[0],
            email: cred.user.email || email,
            picture: cred.user.photoURL || '',
            sub: cred.user.uid,
            role,
            permissions,
        };
    } catch (e: any) {
        console.error('[LOGIN] Error:', e.code, e.message);
        if (e.code === 'auth/user-not-found') {
            throw new Error('No account found with this email. Please sign up first.');
        }
        if (e.code === 'auth/invalid-credential') {
            throw new Error('Invalid email or password.');
        }
        if (e.code === 'auth/invalid-email') {
            throw new Error('Please enter a valid email address.');
        }
        throw e;
    }
};

// Legacy local login code removed - Firebase-only authentication required
const _legacyLocalLogin = async (email: string, password: string) => {
    const db = await openDB();
    const passwordHash = await hashPassword(password);

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([USER_STORE_NAME], 'readonly');
        const store = transaction.objectStore(USER_STORE_NAME);
        const request = store.get(email);

        request.onsuccess = () => {
            const user = request.result as LocalUserCredentials;
            if (!user) {
                reject(new Error('User not found'));
                return;
            }
            if (user.passwordHash !== passwordHash) {
                reject(new Error('Invalid password'));
                return;
            }
            resolve({
                name: user.name,
                email: user.email,
                picture: user.picture || '',
                sub: `local_${email}` 
            });
        };
        request.onerror = () => reject(request.error);
    });
};

export const loginWithGoogle = async (): Promise<UserProfile> => {
    console.log('[GOOGLE_AUTH] Starting Google login...');
    console.log('[GOOGLE_AUTH] isFirebaseActive:', isFirebaseActive());
    console.log('[GOOGLE_AUTH] Current hostname:', window.location.hostname);
    console.log('[GOOGLE_AUTH] Current origin:', window.location.origin);
    console.log('[GOOGLE_AUTH] Firebase authDomain:', firebaseConfig.authDomain);
    
    if (isFirebaseActive()) {
        try {
            console.log('[GOOGLE_AUTH] Firebase is active, attempting popup...');
            const auth = getAuth(firebaseApp!);
            console.log('[GOOGLE_AUTH] Auth config:', auth.config);
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            console.log('[GOOGLE_AUTH] Google login successful:', result.user.uid);
            // Fetch role from Firestore (admin/superadmin removed)
            let role: 'user' | 'admin' | 'superadmin' = 'user';
            const permissions: string[] = [];
            try {
                const db = getFirestore(firebaseApp!);
                const userDoc = await getDoc(doc(db, 'users', result.user.uid));
                if (userDoc.exists() && userDoc.data()?.role) {
                    const r = userDoc.data().role;
                    role = (r === 'admin' || r === 'superadmin') ? 'user' : r;
                }
            } catch (e) {
                console.warn('[GOOGLE_AUTH] Could not fetch user role:', e);
            }
            return {
                name: result.user.displayName || 'User',
                email: result.user.email || '',
                picture: result.user.photoURL || '',
                sub: result.user.uid,
                role,
                permissions,
            };
        } catch (e: any) {
            console.error("[GOOGLE_AUTH] Error:", e.code, e.message);
            if (e.code === 'auth/popup-closed-by-user') {
                throw new Error('Google sign-in was cancelled. Please try again.');
            }
            if (e.code === 'auth/popup-blocked') {
                throw new Error('Google sign-in popup was blocked. Please allow popups and try again.');
            }
            if (e.code === 'auth/cancelled-popup-request') {
                throw new Error('Google sign-in was cancelled.');
            }
            throw new Error(e.message || 'Google sign-in failed. Please check your internet connection and try again.');
        }
    }
    console.error('[GOOGLE_AUTH] Firebase is not active');
    throw new Error("Google Sign In requires Firebase Configuration");
};

export const resetPassword = async (email: string): Promise<{ resetLink?: string; hasGoogle?: boolean }> => {
    if (!isFirebaseActive()) {
        throw new Error("Password reset requires Firebase configuration. Please check your internet connection.");
    }
    
    try {
        console.log('[PASSWORD_RESET] Requesting reset link for:', email);
        
        // Call our Netlify function which uses Firebase Admin SDK
        // This bypasses Firebase's unreliable built-in email sender
        const response = await fetch('/.netlify/functions/send-reset-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to generate password reset link');
        }
        
        console.log('[PASSWORD_RESET] Got reset link successfully');
        return {
            resetLink: data.resetLink,
            hasGoogle: data.hasGoogle,
        };
    } catch (e: any) {
        console.error("[PASSWORD_RESET] Error:", e.message);
        throw new Error(e.message || "Failed to send password reset email. Please try again.");
    }
};

export const logoutUser = async () => {
    resetFirestoreProbe();
    if (isFirebaseActive()) {
        try {
            const auth = getAuth(firebaseApp!);
            await signOut(auth);
        } catch (e) {
            console.warn("Firebase logout failed", e);
        }
    }
}

/**
 * Delete user account and all associated cloud data
 * This permanently deletes:
 * - Firebase Auth account
 * - User document and all subcollections (subscription, usage, settings, files, workspaces)
 * - All files in Firebase Storage for this user
 * - All projects owned by this user
 * 
 * Does NOT delete local storage files
 */
export const deleteUserAccount = async (userId: string): Promise<{ success: boolean; error?: string }> => {
    if (!isFirebaseActive()) {
        return { success: false, error: 'Firebase not active' };
    }
    
    try {
        const auth = getAuth(firebaseApp!);
        const db = getFirestore(firebaseApp!);
        const storage = getStorage(firebaseApp!);
        
        console.log('🗑️ Starting account deletion for user:', userId);
        
        // 1. Delete all user's projects
        console.log('🗑️ Deleting user projects...');
        const projectsQuery = query(collection(db, 'projects'), where('ownerId', '==', userId));
        const projectsSnapshot = await getDocs(projectsQuery);
        const projectDeletePromises = projectsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(projectDeletePromises);
        console.log(`🗑️ Deleted ${projectsSnapshot.size} projects`);
        
        // 2. Delete all files in Firebase Storage for this user
        console.log('🗑️ Deleting storage files...');
        try {
            const userStorageRef = ref(storage, `users/${userId}`);
            const storageFiles = await listAll(userStorageRef);
            
            // Delete files recursively (listAll doesn't return nested items, so we need to handle folders)
            const deleteStorageRecursive = async (folderRef: any) => {
                const result = await listAll(folderRef);
                
                // Delete all files in this folder
                for (const itemRef of result.items) {
                    try {
                        await deleteObject(itemRef);
                    } catch (e) {
                        console.warn('Could not delete file:', itemRef.fullPath, e);
                    }
                }
                
                // Recurse into subfolders
                for (const prefixRef of result.prefixes) {
                    await deleteStorageRecursive(prefixRef);
                }
            };
            
            await deleteStorageRecursive(userStorageRef);
            console.log('🗑️ Deleted storage files');
        } catch (e) {
            console.warn('Could not delete storage files (may not exist):', e);
        }
        
        // 3. Delete Firestore subcollections
        console.log('🗑️ Deleting user subcollections...');
        const subcollections = ['subscription', 'usage', 'settings', 'files', 'workspaces'];
        
        for (const subcollName of subcollections) {
            try {
                const subcollRef = collection(db, 'users', userId, subcollName);
                const subcollDocs = await getDocs(subcollRef);
                const deletePromises = subcollDocs.docs.map(doc => deleteDoc(doc.ref));
                await Promise.all(deletePromises);
                console.log(`🗑️ Deleted ${subcollDocs.size} docs from ${subcollName}`);
            } catch (e) {
                console.warn(`Could not delete ${subcollName} subcollection:`, e);
            }
        }
        
        // 4. Delete user document
        console.log('🗑️ Deleting user document...');
        try {
            await deleteDoc(doc(db, 'users', userId));
        } catch (e) {
            console.warn('Could not delete user document:', e);
        }
        
        // 5. Delete Firebase Auth account
        console.log('🗑️ Deleting auth account...');
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === userId) {
            await deleteUser(currentUser);
            console.log('🗑️ Auth account deleted');
        } else {
            console.warn('Current user does not match userId, skipping auth deletion');
        }
        
        console.log('✅ Account deletion complete');
        return { success: true };
        
    } catch (e: any) {
        console.error('❌ Account deletion failed:', e);
        
        // Handle re-authentication requirement
        if (e.code === 'auth/requires-recent-login') {
            return { 
                success: false, 
                error: 'For security, please sign out and sign back in before deleting your account.' 
            };
        }
        
        return { success: false, error: e.message || 'Failed to delete account' };
    }
};


// --- SUBSCRIPTION MANAGEMENT ---

/**
 * Fetch user's subscription from Firestore
 */
export const getSubscription = async (userId: string): Promise<UserSubscription | null> => {
    if (!isFirebaseActive()) return null;
    
    try {
        const db = getFirestore(firebaseApp!);
        const subscriptionRef = doc(db, 'users', userId, 'subscription', 'current');
        const subscriptionDoc = await getDoc(subscriptionRef);
        
        if (subscriptionDoc.exists()) {
            const subscription = subscriptionDoc.data() as UserSubscription;
            
            // Check if user has credits but no active period set (needs activation)
            if (subscription.credits && subscription.credits > 0 && !subscription.currentCreditExpiresAt) {
                // Activate first credit
                await activateFirstCredit(userId);
                // Re-fetch updated subscription
                const updatedDoc = await getDoc(subscriptionRef);
                return updatedDoc.exists() ? updatedDoc.data() as UserSubscription : null;
            }
            
            // Check if credit period has expired and needs update
            if (subscription.credits && subscription.credits > 0 && 
                subscription.currentCreditExpiresAt && 
                Date.now() > subscription.currentCreditExpiresAt) {
                // Auto-consume next credit if available
                await consumeMonthlyCredit(userId);
                // Re-fetch updated subscription
                const updatedDoc = await getDoc(subscriptionRef);
                return updatedDoc.exists() ? updatedDoc.data() as UserSubscription : null;
            }
            return subscription;
        }
        return null;
    } catch (e) {
        console.error("Failed to fetch subscription:", e);
        return null;
    }
};

/**
 * Consume a monthly credit for pro access
 * Called when the current credit period expires
 */
// --- LOCAL STORAGE PREFERENCES (CROSS-DEVICE) ---

export interface LocalStoragePrefs {
    hasLocalStorageEnabled: boolean;
    localStoragePath: string | null;
    lastEnabledAt: number | null;
}

/**
 * Save local storage preferences to Firebase user profile
 * This allows detection across devices/browsers
 */
export const saveLocalStoragePrefs = async (
    userId: string, 
    prefs: Partial<LocalStoragePrefs>
): Promise<boolean> => {
    if (!isFirebaseActive()) return false;
    
    try {
        const db = getFirestore(firebaseApp!);
        const prefsRef = doc(db, 'users', userId, 'settings', 'localStoragePrefs');
        await setDoc(prefsRef, {
            ...prefs,
            updatedAt: Date.now()
        }, { merge: true });
        return true;
    } catch (e: any) {
        // Silently handle permission errors - this is expected if rules aren't set up
        if (e?.code === 'permission-denied') {
            console.log("Cannot save local storage prefs (Firestore rules may need updating)");
            return false;
        }
        console.error("Failed to save local storage prefs:", e);
        return false;
    }
};

/**
 * Get local storage preferences from Firebase user profile
 */
export const getLocalStoragePrefs = async (userId: string): Promise<LocalStoragePrefs | null> => {
    if (!isFirebaseActive()) return null;
    
    try {
        const db = getFirestore(firebaseApp!);
        const prefsRef = doc(db, 'users', userId, 'settings', 'localStoragePrefs');
        const prefsDoc = await getDoc(prefsRef);
        
        if (!prefsDoc.exists()) {
            return null;
        }
        
        return prefsDoc.data() as LocalStoragePrefs;
    } catch (e: any) {
        // Silently handle permission errors - this is expected if rules aren't set up
        if (e?.code === 'permission-denied') {
            console.log("Local storage prefs not accessible (Firestore rules may need updating)");
            return null;
        }
        console.error("Failed to get local storage prefs:", e);
        return null;
    }
};

// --- ONBOARDING TRACKING ---

export interface OnboardingStatus {
    completed: boolean;
    localStoragePromptShown: boolean;
    completedAt?: number;
}

/**
 * Get user's onboarding status
 */
export const getOnboardingStatus = async (userId: string): Promise<OnboardingStatus | null> => {
    if (!isFirebaseActive()) return null;
    
    try {
        const db = getFirestore(firebaseApp!);
        const onboardingRef = doc(db, 'users', userId, 'settings', 'onboarding');
        const onboardingDoc = await getDoc(onboardingRef);
        
        if (!onboardingDoc.exists()) {
            return null; // New user, hasn't seen onboarding
        }
        
        return onboardingDoc.data() as OnboardingStatus;
    } catch (e: any) {
        if (e?.code === 'permission-denied') {
            return null;
        }
        console.error("Failed to get onboarding status:", e);
        return null;
    }
};

/**
 * Mark onboarding as completed
 */
export const completeOnboarding = async (userId: string): Promise<boolean> => {
    if (!isFirebaseActive()) return false;
    
    try {
        const db = getFirestore(firebaseApp!);
        const onboardingRef = doc(db, 'users', userId, 'settings', 'onboarding');
        await setDoc(onboardingRef, {
            completed: true,
            localStoragePromptShown: true,
            completedAt: Date.now()
        }, { merge: true });
        return true;
    } catch (e: any) {
        console.error("Failed to save onboarding status:", e);
        return false;
    }
};

/**
 * Ask the server to move the subscription along: the next period starts (spending a credit)
 * once the running one has ended and a credit is left; the plan is free once none is left.
 * The record is the server's to write (firestore.rules), so the browser only asks.
 */
const activateSubscriptionPeriod = async (): Promise<UserSubscription | null> => {
    if (!isFirebaseActive() || isOffline()) return null;
    const token = await getAuthToken();
    if (!token) return null;
    try {
        const res = await fetch(`${getApiBase()}/api/subscription/activate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            console.warn('[subscription] the period could not be moved along:', res.status);
            return null;
        }
        const data = await res.json();
        return (data?.subscription as UserSubscription) ?? null;
    } catch (e) {
        console.warn('[subscription] the period could not be moved along:', e);
        return null;
    }
};

/** Start the next period with a credit once the running one has ended. */
export const consumeMonthlyCredit = async (_userId: string): Promise<{ success: boolean; remainingCredits: number }> => {
    const subscription = await activateSubscriptionPeriod();
    return { success: !!subscription && subscription.status === 'active', remainingCredits: subscription?.credits || 0 };
};

/** Start the first period with a credit, for a subscription that has credits and no period yet. */
export const activateFirstCredit = async (_userId: string): Promise<boolean> => {
    const subscription = await activateSubscriptionPeriod();
    return !!subscription && subscription.status === 'active' && !!subscription.currentCreditExpiresAt;
};

// Paying for Pro is in utils/payments.ts: the server credits the account once it sees the
// transfer (netlify/functions/api/pay.ts). The browser never writes credits itself.

/**
 * Subscribe to real-time subscription updates
 */
export const subscribeToSubscription = (
    userId: string, 
    callback: (subscription: UserSubscription | null) => void
): (() => void) => {
    if (!isFirebaseActive()) {
        callback(null);
        return () => {};
    }
    
    try {
        const db = getFirestore(firebaseApp!);
        const subscriptionRef = doc(db, 'users', userId, 'subscription', 'current');
        
        return onSnapshot(subscriptionRef, (doc) => {
            if (doc.exists()) {
                callback(doc.data() as UserSubscription);
            } else {
                callback(null);
            }
        }, (error) => {
            console.error("Subscription listener error:", error);
            callback(null);
        });
    } catch (e) {
        console.error("Failed to set up subscription listener:", e);
        callback(null);
        return () => {};
    }
};

// --- STORAGE USAGE TRACKING ---

export interface StorageUsage {
    totalBytes: number;
    lastUpdated: number;
    fileCount: number;
}

/**
 * Get current storage usage for a user
 */
export const getStorageUsage = async (userId: string): Promise<StorageUsage> => {
    if (!isFirebaseActive()) {
        return { totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 };
    }
    
    try {
        const db = getFirestore(firebaseApp!);
        const usageRef = doc(db, 'users', userId, 'usage', 'storage');
        const usageDoc = await getDoc(usageRef);
        
        if (usageDoc.exists()) {
            return usageDoc.data() as StorageUsage;
        }
        return { totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 };
    } catch (e) {
        console.error("Failed to get storage usage:", e);
        return { totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 };
    }
};

/**
 * Update storage usage after uploading a file
 * @param userId User ID
 * @param bytesAdded Number of bytes to add (can be negative for deletions)
 * @param filesAdded Number of files to add (can be negative for deletions)
 */
export const updateStorageUsage = async (
    userId: string, 
    bytesAdded: number, 
    filesAdded: number = 1
): Promise<StorageUsage> => {
    if (!isFirebaseActive()) {
        return { totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 };
    }
    
    try {
        const db = getFirestore(firebaseApp!);
        const usageRef = doc(db, 'users', userId, 'usage', 'storage');
        const usageDoc = await getDoc(usageRef);
        
        const currentUsage = usageDoc.exists() 
            ? usageDoc.data() as StorageUsage 
            : { totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 };
        
        const newUsage: StorageUsage = {
            totalBytes: Math.max(0, currentUsage.totalBytes + bytesAdded),
            fileCount: Math.max(0, currentUsage.fileCount + filesAdded),
            lastUpdated: Date.now(),
        };
        
        await setDoc(usageRef, newUsage);
        console.log(`Storage usage updated: ${(newUsage.totalBytes / (1024 * 1024)).toFixed(2)} MB, ${newUsage.fileCount} files`);
        return newUsage;
    } catch (e) {
        console.error("Failed to update storage usage:", e);
        return { totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 };
    }
};

/**
 * Check if user has enough storage space
 * @param userId User ID
 * @param bytesNeeded Bytes that will be uploaded
 * @param maxStorageMB Maximum storage in MB from subscription limits
 */
export const checkStorageLimit = async (
    userId: string, 
    bytesNeeded: number, 
    maxStorageMB: number
): Promise<{ allowed: boolean; currentUsageMB: number; maxMB: number }> => {
    const usage = await getStorageUsage(userId);
    const currentUsageMB = usage.totalBytes / (1024 * 1024);
    const projectedUsageMB = (usage.totalBytes + bytesNeeded) / (1024 * 1024);
    
    return {
        allowed: projectedUsageMB <= maxStorageMB,
        currentUsageMB,
        maxMB: maxStorageMB,
    };
};

/**
 * Subscribe to real-time storage usage updates
 */
export const subscribeToStorageUsage = (
    userId: string,
    callback: (usage: StorageUsage) => void
): (() => void) => {
    if (!isFirebaseActive()) {
        callback({ totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 });
        return () => {};
    }
    
    try {
        const db = getFirestore(firebaseApp!);
        const usageRef = doc(db, 'users', userId, 'usage', 'storage');
        
        return onSnapshot(usageRef, (docSnap) => {
            if (docSnap.exists()) {
                callback(docSnap.data() as StorageUsage);
            } else {
                callback({ totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 });
            }
        }, (error) => {
            console.error("Storage usage listener error:", error);
            callback({ totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 });
        });
    } catch (e) {
        console.error("Failed to set up storage usage listener:", e);
        callback({ totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 });
        return () => {};
    }
};

/**
 * Recalculate storage usage by scanning all files in Firebase Storage
 * This is useful to sync the tracked usage with actual storage
 * @param userId User ID
 * @param onProgress Optional callback for progress updates
 */
export const recalculateStorageUsage = async (
    userId: string,
    onProgress?: (current: number, total: number) => void
): Promise<StorageUsage> => {
    if (!isFirebaseActive()) {
        return { totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 };
    }
    
    try {
        const storage = getStorage(firebaseApp!);
        const userStorageRef = ref(storage, `users/${userId}`);
        
        let totalBytes = 0;
        let fileCount = 0;
        const filesToProcess: string[] = [];
        
        // Recursively list all files
        const listFilesRecursively = async (folderRef: any): Promise<void> => {
            try {
                const result = await listAll(folderRef);
                
                // Add all file paths to our list
                for (const item of result.items) {
                    filesToProcess.push(item.fullPath);
                }
                
                // Recursively process subfolders
                for (const prefix of result.prefixes) {
                    await listFilesRecursively(prefix);
                }
            } catch (e) {
                console.warn("Error listing folder:", e);
            }
        };
        
        console.log(`📊 Starting storage recalculation for user ${userId}...`);
        await listFilesRecursively(userStorageRef);
        
        const totalFiles = filesToProcess.length;
        console.log(`📁 Found ${totalFiles} files to process`);
        
        // Get metadata for each file
        for (let i = 0; i < filesToProcess.length; i++) {
            try {
                const fileRef = ref(storage, filesToProcess[i]);
                const metadata = await getMetadata(fileRef);
                totalBytes += metadata.size || 0;
                fileCount++;
                
                if (onProgress) {
                    onProgress(i + 1, totalFiles);
                }
            } catch (e) {
                console.warn(`Failed to get metadata for ${filesToProcess[i]}:`, e);
            }
        }
        
        // Update the stored usage
        const newUsage: StorageUsage = {
            totalBytes,
            fileCount,
            lastUpdated: Date.now(),
        };
        
        const db = getFirestore(firebaseApp!);
        const usageRef = doc(db, 'users', userId, 'usage', 'storage');
        await setDoc(usageRef, newUsage);
        
        console.log(`✅ Storage recalculation complete: ${(totalBytes / (1024 * 1024)).toFixed(2)} MB, ${fileCount} files`);
        return newUsage;
    } catch (e) {
        console.error("Failed to recalculate storage usage:", e);
        return { totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 };
    }
};

/**
 * Delete a file from Firebase Storage and update usage tracking
 * @param userId User ID  
 * @param filePath Full path to the file in storage
 */
export const deleteStorageFile = async (
    userId: string,
    filePath: string
): Promise<boolean> => {
    if (!isFirebaseActive()) return false;
    
    try {
        const storage = getStorage(firebaseApp!);
        const fileRef = ref(storage, filePath);
        
        // Get file size before deleting
        let fileSize = 0;
        try {
            const metadata = await getMetadata(fileRef);
            fileSize = metadata.size || 0;
        } catch (e) {
            console.warn("Could not get file metadata before deletion:", e);
        }
        
        // Delete the file
        await deleteObject(fileRef);
        
        // Update storage usage (subtract the deleted file size)
        if (fileSize > 0) {
            await updateStorageUsage(userId, -fileSize, -1);
        }
        
        console.log(`🗑️ Deleted file: ${filePath} (${(fileSize / 1024).toFixed(2)} KB)`);
        return true;
    } catch (e) {
        console.error("Failed to delete storage file:", e);
        return false;
    }
};

// --- STORAGE MIGRATION UTILITY ---

/**
 * Determine the category of a file based on its old path/filename
 */
const getCategoryFromOldPath = (filename: string): StorageCategory => {
    if (filename.startsWith('gen_') || filename === 'gen_main.png') return 'Generated_Images';
    if (filename.startsWith('ref_')) return 'Characters'; // Default refs to Characters, will be corrected by node type
    if (filename === 'video.mp4' || filename.endsWith('.mp4') || filename.endsWith('.webm')) return 'Videos';
    if (filename.includes('sketch')) return 'Sketches';
    return 'Uploaded_Images';
};

/**
 * Get file extension from content type or filename
 */
const getExtensionFromFile = (filename: string, contentType?: string): string => {
    if (contentType) {
        if (contentType.includes('video/mp4')) return 'mp4';
        if (contentType.includes('video/webm')) return 'webm';
        if (contentType.includes('image/png')) return 'png';
        if (contentType.includes('image/jpeg')) return 'jpg';
        if (contentType.includes('image/gif')) return 'gif';
        if (contentType.includes('image/webp')) return 'webp';
    }
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || 'png';
};

/**
 * Migrate existing storage from old node-ID-based structure to new workspace-based structure
 * Old: users/{userId}/nodes/{nodeId}/gen_0.png
 * New: users/{userId}/{workspaceName}/Generated_Images/generated-images_timestamp.png
 */
export const migrateStorageStructure = async (
    userId: string,
    workspaceName: string,
    onProgress?: (current: number, total: number, status: string) => void
): Promise<{ migratedFiles: number; errors: string[] }> => {
    if (!isFirebaseActive()) {
        return { migratedFiles: 0, errors: ['Firebase not active'] };
    }
    
    const errors: string[] = [];
    let migratedFiles = 0;
    
    try {
        const storage = getStorage(firebaseApp!);
        
        // List all files in old structure: users/{userId}/nodes/
        const oldBasePath = `users/${userId}/nodes`;
        const oldBaseRef = ref(storage, oldBasePath);
        
        onProgress?.(0, 0, 'Scanning old storage structure...');
        
        // Recursively collect all files in old structure
        const filesToMigrate: { ref: any; filename: string; fullPath: string }[] = [];
        
        const listFilesRecursively = async (folderRef: any): Promise<void> => {
            try {
                const result = await listAll(folderRef);
                
                for (const item of result.items) {
                    filesToMigrate.push({
                        ref: item,
                        filename: item.name,
                        fullPath: item.fullPath
                    });
                }
                
                for (const prefix of result.prefixes) {
                    await listFilesRecursively(prefix);
                }
            } catch (e) {
                // Folder might not exist, that's OK
                console.log("No old storage structure found or error listing:", e);
            }
        };
        
        await listFilesRecursively(oldBaseRef);
        
        if (filesToMigrate.length === 0) {
            onProgress?.(0, 0, 'No files to migrate');
            return { migratedFiles: 0, errors: [] };
        }
        
        onProgress?.(0, filesToMigrate.length, `Found ${filesToMigrate.length} files to migrate`);
        
        // Migrate each file
        for (let i = 0; i < filesToMigrate.length; i++) {
            const file = filesToMigrate[i];
            
            try {
                onProgress?.(i + 1, filesToMigrate.length, `Migrating: ${file.filename}`);
                
                // Get the file's download URL and metadata
                const [downloadUrl, metadata] = await Promise.all([
                    getDownloadURL(file.ref),
                    getMetadata(file.ref)
                ]);
                
                // Determine category based on filename
                const category = getCategoryFromOldPath(file.filename);
                const extension = getExtensionFromFile(file.filename, metadata.contentType);
                
                // Generate new path
                const timestamp = Date.now() + i; // Ensure unique timestamps
                const newPath = getStoragePath(userId, workspaceName, category, timestamp, extension);
                
                // Download the file content; never migrate an error body in place of the file
                const response = await fetch(downloadUrl);
                if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
                const blob = await response.blob();
                
                // Convert to base64 for upload
                const base64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
                
                // Upload to new location
                const newRef = ref(storage, newPath);
                await uploadString(newRef, base64, 'data_url');
                
                // Delete old file
                await deleteObject(file.ref);
                
                migratedFiles++;
                
            } catch (e: any) {
                console.error(`Failed to migrate file ${file.fullPath}:`, e);
                errors.push(`${file.filename}: ${e.message}`);
            }
        }
        
        // Clean up empty folders (Firebase doesn't have actual folders, they disappear when empty)
        onProgress?.(filesToMigrate.length, filesToMigrate.length, 'Migration complete!');
        
        return { migratedFiles, errors };
        
    } catch (e: any) {
        console.error("Migration failed:", e);
        return { migratedFiles, errors: [...errors, `Migration error: ${e.message}`] };
    }
};

/**
 * Check if user has files in old storage structure that need migration
 */
export const checkNeedsMigration = async (userId: string): Promise<boolean> => {
    if (!isFirebaseActive()) return false;
    
    try {
        const storage = getStorage(firebaseApp!);
        const oldBasePath = `users/${userId}/nodes`;
        const oldBaseRef = ref(storage, oldBasePath);
        
        const result = await listAll(oldBaseRef);
        
        // Check if there are any folders (node IDs) in the old structure
        return result.prefixes.length > 0 || result.items.length > 0;
    } catch (e) {
        // If folder doesn't exist, no migration needed
        return false;
    }
};

/**
 * Subscribe to real-time project updates for live sync across devices
 * Only works for cloud-synced projects (Pro users)
 * Uses sessionId to skip self-originated updates (echo suppression)
 *
 * The callback first hears the workspace as the cloud holds it when the listeners start, then
 * every save made elsewhere. `nodes` holds every node each time; a node whose document did
 * not change is the same object as the time before.
 */
export const subscribeToProject = (
    projectId: string,
    callback: (data: {
        nodes: CanvasNode[];
        connections: Connection[];
        history: HistoryItem[];
        transform: ViewportTransform;
        name: string;
        lastSaved: number;
        /** Whether the project document was last written by another session than this one (its view is that session's). */
        fromOtherSession: boolean;
    } | null) => void
): (() => void) => {
    if (!isFirebaseActive()) {
        return () => {};
    }
    
    // Guard: Firestore rules require authentication for reading project data.
    // If no user is signed in yet, skip subscribing to avoid permission errors.
    const auth = getAuth(firebaseApp!);
    if (!auth.currentUser) {
        console.warn('subscribeToProject: No authenticated user, skipping listener setup');
        return () => {};
    }
    
    // Force token refresh so Firestore SDK has the auth credential before listeners start
    try {
        // getIdToken() is synchronous if token is cached and valid, otherwise refreshes
        auth.currentUser.getIdToken().catch(() => {});
    } catch (_) {}
    
    const currentSessionId = getSyncSessionId();
    
    try {
        const db = getFirestore(firebaseApp!);
        const projectRef = doc(db, 'projects', projectId);
        
        // Track accumulated nodes state for v2 incremental updates. A node keeps its object here
        // until its document changes, so the caller can tell what an update really changed.
        const currentNodes = new Map<string, CanvasNode>();
        let nodesReady = false;
        // Node snapshots are taken in one at a time: rehydrating is asynchronous, and two under way at once could finish out of order
        let nodeWork: Promise<void> = Promise.resolve();
        // The project document as the metadata listener last heard it
        let latestMeta: Record<string, any> | null = null;
        // The state of the workspace when the listeners started is sent once, whoever saved it last
        let initialSent = false;

        // Track whether listeners have been cleaned up due to permission errors
        let cleanedUp = false;
        const cleanupAll = () => {
            if (cleanedUp) return;
            cleanedUp = true;
            try { unsubMeta(); } catch (_) {}
            try { unsubNodes(); } catch (_) {}
        };

        const fromThisSession = (meta: Record<string, any>) => meta.lastEditedBySession === currentSessionId;
        const emit = (meta: Record<string, any>, nodes: CanvasNode[]) => {
            if (cleanedUp) return;
            callback({
                nodes,
                connections: meta.connections || [],
                history: meta.history || [],
                transform: meta.transform || { x: 0, y: 0, scale: 1 },
                name: meta.name || 'Untitled',
                lastSaved: meta.lastSaved || 0,
                fromOtherSession: !fromThisSession(meta),
            });
        };

        /**
         * v2: a save changes the project document and node documents together, and each
         * listener hears of its part on its own. The workspace is sent on once both have, as
         * one update that holds together, and not at all for this session's own saves
         * (echo suppression).
         */
        let settling = false;
        // Something more was heard while the last was being taken in
        let heardMore = false;
        const settle = () => {
            if (settling) {
                heardMore = true;
                return;
            }
            settling = true;
            // A turn later: after the other listener has heard of the same save
            setTimeout(async () => {
                try {
                    let meta: Record<string, any> | null;
                    // How often a save was seen that the listeners had not heard of yet
                    let unheard = 0;
                    // Nothing is sent while a snapshot is still being taken in: half of a save
                    // (its connections without the node they lead to) must not reach the canvas
                    do {
                        heardMore = false;
                        await nodeWork;
                        if (cleanedUp) return;
                        meta = latestMeta;
                        try {
                            // The project document as it is now: answered by the listener above, not by the network
                            const metaSnap = await getDoc(projectRef);
                            if (metaSnap.exists()) {
                                const current = metaSnap.data();
                                // A save neither listener has been told of yet (they are told a
                                // turn after the client knows): its nodes are not in the map
                                // either, so it is waited for. Not for ever, in case a listener
                                // has gone quiet: then what is known is sent as it is.
                                if (latestMeta && current.version !== latestMeta.version && unheard < 25) {
                                    unheard++;
                                    heardMore = true;
                                } else {
                                    meta = current;
                                }
                            }
                        } catch (_) {}
                        // (the other listener hears of that save a turn later)
                        if (heardMore) await new Promise<void>(resolve => setTimeout(resolve, unheard ? 4 : 0));
                    } while (heardMore);
                    if (cleanedUp || !nodesReady) return;
                    if (!meta || meta.schemaVersion !== 2) return;
                    if (initialSent && fromThisSession(meta)) return;
                    initialSent = true;
                    emit(meta, Array.from(currentNodes.values()));
                } finally {
                    settling = false;
                }
            }, 0);
        };

        // Listen to project metadata
        const unsubMeta = onSnapshot(projectRef, async (docSnap) => {
            if (!docSnap.exists()) {
                latestMeta = null;
                callback(null);
                return;
            }

            const data = docSnap.data();
            latestMeta = data;

            if (data.schemaVersion === 2) {
                // v2: metadata only — nodes come from the sub-collection listener
                // (nothing is sent before they have arrived)
                settle();
            } else {
                // Skip self-originated updates (echo suppression)
                if (fromThisSession(data)) return;
                // v1 legacy: nodes are inline in the project document
                const rehydratedNodes = await rehydrateNodes(data.nodes || []);
                emit(data, rehydratedNodes);
            }
        }, (error) => {
            if (error.code === 'permission-denied') {
                console.warn('subscribeToProject: Permission denied for project metadata — listener disabled but writes remain enabled');
                cleanupAll();
            } else {
                console.error('Project metadata listener error:', error);
            }
        });

        // Listen to nodes sub-collection (v2 — fires even if project is v1, just won't have docs)
        const nodesRef = collection(db, 'projects', projectId, 'nodes');
        const applyNodeChanges = async (snapshot: QuerySnapshot) => {
            // Every snapshot goes into the map, this session's own saves too: left out, the
            // map went stale and the next update from elsewhere brought back nodes as they
            // were before those saves.
            for (const change of snapshot.docChanges()) {
                switch (change.type) {
                    case 'added':
                    case 'modified': {
                        const nodeData = change.doc.data() as CanvasNode;
                        // Rehydrate the individual changed node
                        try {
                            const rehydrated = await rehydrateNodes([nodeData]);
                            currentNodes.set(change.doc.id, rehydrated[0]);
                        } catch (e) {
                            currentNodes.set(change.doc.id, nodeData);
                        }
                        break;
                    }
                    case 'removed':
                        currentNodes.delete(change.doc.id);
                        break;
                }
            }
            // A snapshot answered from this client's cache (the listeners started while offline)
            // is not what the cloud holds: it would send the workspace on with no nodes, and the
            // canvas, and then the cloud, would be emptied. Nothing is sent before the server has answered.
            if (!snapshot.metadata?.fromCache) nodesReady = true;
        };
        // (With metadata changes, so that the server's first answer is heard even when it changes
        // no document; later events that change no document are nothing new.)
        const unsubNodes = onSnapshot(nodesRef, { includeMetadataChanges: true }, (snapshot) => {
            if (nodesReady && snapshot.docChanges().length === 0) return;
            nodeWork = nodeWork.then(() => applyNodeChanges(snapshot)).catch((e) => {
                console.error('Nodes sub-collection update failed:', e);
            });
            settle();
        }, (error) => {
            if (error.code === 'permission-denied') {
                console.warn('subscribeToProject: Permission denied for nodes sub-collection — listener disabled but writes remain enabled');
                cleanupAll();
            } else {
                console.error('Nodes sub-collection listener error:', error);
            }
        });
        
        // Return combined unsubscribe
        return () => {
            cleanupAll();
        };
    } catch (e) {
        console.error("Failed to set up project listener:", e);
        return () => {};
    }
};

/**
 * Get daily AI generation count for a user (for rate limiting free tier)
 */
export const getDailyGenerationCount = async (userId: string): Promise<number> => {
    if (!isFirebaseActive()) return 0;
    
    try {
        const db = getFirestore(firebaseApp!);
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const usageRef = doc(db, 'users', userId, 'usage', today);
        const usageDoc = await getDoc(usageRef);
        
        if (usageDoc.exists()) {
            return usageDoc.data()?.aiGenerations || 0;
        }
        return 0;
    } catch (e) {
        console.error("Failed to fetch generation count:", e);
        return 0;
    }
};

/**
 * Increment daily AI generation count
 */
export const incrementGenerationCount = async (userId: string): Promise<number> => {
    if (!isFirebaseActive()) return 0;
    
    try {
        const db = getFirestore(firebaseApp!);
        const today = new Date().toISOString().split('T')[0];
        const usageRef = doc(db, 'users', userId, 'usage', today);
        const usageDoc = await getDoc(usageRef);
        
        const currentCount = usageDoc.exists() ? (usageDoc.data()?.aiGenerations || 0) : 0;
        const newCount = currentCount + 1;
        
        await setDoc(usageRef, { 
            aiGenerations: newCount,
            date: today,
            updatedAt: Date.now()
        }, { merge: true });
        
        return newCount;
    } catch (e) {
        console.error("Failed to increment generation count:", e);
        return 0;
    }
};


// --- PROJECT STORAGE ---

export const blobUrlToBase64 = async (url: string): Promise<string> => {
  if (!url || !url.startsWith('blob:')) return url;
  try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
  } catch (e) {
      console.warn("Failed to convert blob to base64", e);
      return url;
  }
};

const base64ToBlobUrl = async (base64: string): Promise<string> => {
    if (!base64 || !base64.startsWith('data:')) return base64; 
    // Optimization: Only convert heavy media types like video to blob
    // However, if we're in a rehydration loop, we might want to be careful not to crash
    if (base64.startsWith('data:video')) {
        try {
            const res = await fetch(base64);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            markRehydratedBlob(url); // a live copy, not a blob URL left over from an earlier session
            return url;
        } catch (e) {
            console.error("Failed to convert base64 video to blob", e);
            return base64; // Fallback to base64 string, video tag can handle it usually
        }
    }
    return base64;
};

// Rehydrate: Process nodes from import/load to be ready for app
export const rehydrateNodes = async (nodes: CanvasNode[]): Promise<CanvasNode[]> => {
    if (!nodes || !Array.isArray(nodes)) return [];
    
    // Process sequentially or independently to prevent one error from failing all
    try {
        const processed = await Promise.all(nodes.map(async node => {
            try {
                // Clone node to avoid mutation side effects
                const newNode = JSON.parse(JSON.stringify(node));
                
                // IMPORTANT: Reset any stuck generation states
                // This prevents nodes from being stuck in "Dreaming..." after reload.
                // Async video jobs (Sora, Venice) carry a job id and resume on mount instead.
                if (newNode.data?.generationStatus === 'generating' && !newNode.data.jobId) {
                    console.log(`Resetting stuck generation status for node ${newNode.id}`);
                    newNode.data.generationStatus = 'idle';
                }
                
                // Rehydrate Video URIs
                if (newNode.data?.videoUri) {
                    newNode.data.videoUri = await base64ToBlobUrl(newNode.data.videoUri);
                }
                
                // Rehydrate Generated Image URIs (if they were blobs, though usually base64)
                if (newNode.data?.generatedImageUri) {
                     // Normally images are fine as base64, but if we stored them as blob urls in export they were converted to base64.
                     // We keep them as base64 or blob.
                }

                return newNode;
            } catch (e) {
                console.error("Failed to rehydrate node:", node.id, e);
                return node; // Return original node as fallback
            }
        }));
        
        // Final sanity check: if input had items but processed is empty (unlikely with map), return input
        if (nodes.length > 0 && processed.length === 0) return nodes;
        return processed;
        
    } catch (err) {
        console.error("Critical error in rehydrateNodes:", err);
        return nodes; // Fallback to raw nodes
    }
};

// Helper to calculate file size from base64 data URL
const getBase64FileSize = (dataUrl: string): number => {
    // data:image/png;base64,XXXXX -> extract base64 part
    const base64Part = dataUrl.split(',')[1];
    if (!base64Part) return 0;
    // Base64 encoding increases size by ~33%, so actual bytes = base64Length * 3/4
    const padding = (base64Part.match(/=/g) || []).length;
    return Math.floor((base64Part.length * 3) / 4) - padding;
};

// --- LOCAL MEDIA ADDRESSES ---
// In the desktop app a file of the local folder is shown from an address of the app's own
// server (see utils/desktopFolder.ts). The address only works while that run of the app
// lasts, so it is never saved: where a view left one in a node's data, the file reference
// it stands for is saved in its place.

/** Which file reference a local address was resolved from in this session. */
let referenceOfLocalAddress: (url: string) => string | undefined = () => undefined;

/** Loaded on demand: the module that knows imports localSave, which imports this module. */
const loadLocalAddressLookup = async (): Promise<void> => {
    try {
        const { referenceOfDisplayUrl } = await import('./displayUrl');
        referenceOfLocalAddress = referenceOfDisplayUrl;
    } catch (e) {
        // Without it no reference is known, and the addresses are left out of what is saved
        console.warn('Could not load the references of local addresses:', e);
    }
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (value === null || typeof value !== 'object') return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

const holdsLocalAddress = (value: unknown): boolean => {
    if (typeof value === 'string') return isLocalMediaUrl(value);
    if (Array.isArray(value)) return value.some(holdsLocalAddress);
    return isPlainObject(value) && Object.values(value).some(holdsLocalAddress);
};

/**
 * The same value with every local address replaced by the file reference it stands for.
 * `unknown` says what becomes of an address whose reference is not known: 'keep' leaves it
 * (a cloud write then leaves that key out, see sanitizeForFirestore), 'drop' empties it, as
 * for a copy kept on this device. Parts that hold no such address are returned as they are,
 * not copied, and nothing given is changed.
 */
const withStoredReferences = <T,>(value: T, unknown: 'keep' | 'drop'): T => {
    if (typeof value === 'string') {
        if (!isLocalMediaUrl(value)) return value;
        return (referenceOfLocalAddress(value) ?? (unknown === 'drop' ? '' : value)) as unknown as T;
    }
    if (Array.isArray(value)) {
        let changed = false;
        const out = value.map((item) => {
            const next = withStoredReferences(item, unknown);
            if (next !== item) changed = true;
            return next;
        });
        return (changed ? out : value) as unknown as T;
    }
    if (isPlainObject(value)) {
        let out: Record<string, unknown> | null = null;
        for (const [key, item] of Object.entries(value)) {
            const next = withStoredReferences(item, unknown);
            if (next !== item) (out ??= { ...value })[key] = next;
        }
        return (out ?? value) as T;
    }
    return value;
};

/**
 * A node the save did not process, for this device's copy: a blob: URL dies with this run, so
 * the file reference it was uploaded as, or what the copy held before under that key, is stored
 * in its place.
 */
const withoutSessionBlobs = (node: CanvasNode, before?: CanvasNode): CanvasNode => {
    const data = node.data as unknown as Record<string, unknown> | undefined;
    if (!data) return node;
    let out: Record<string, unknown> | null = null;
    for (const [key, value] of Object.entries(data)) {
        if (typeof value !== 'string' || !value.startsWith('blob:')) continue;
        const kept = resolveUploadedBlob(value) ?? (before?.data as unknown as Record<string, unknown> | undefined)?.[key];
        if (typeof kept === 'string' && !kept.startsWith('blob:')) (out ??= { ...data })[key] = kept;
    }
    return out ? { ...node, data: out as unknown as NodeData } : node;
};

// Data Prep for Saving - with stable file references
const prepareDataForStorage = async (
    data: NodeData, 
    userId: string, 
    nodeId: string,
    nodeType: NodeType,
    workspaceName: string,
    /** Collects the ids of nodes whose media could not be uploaded: the cloud then holds nothing for it. */
    unstored?: Set<string>
): Promise<NodeData> => {
    // A local address a view left in the data is saved as the file reference it stands for
    if (holdsLocalAddress(data)) await loadLocalAddressLookup();
    const newData = { ...withStoredReferences(data, 'keep') };
    let totalBytesUploaded = 0;
    let filesUploaded = 0;
    
    // If Firebase is active, attempt upload assets and register them
    if (isFirebaseActive()) {
        /**
         * Upload a file and register it in the file registry with DEDUPLICATION
         * Returns a stable file:// reference instead of a direct URL
         * Uses content hash to prevent duplicate uploads
         */
        const uploadAndRegisterAsset = async (
            dataUrl: string,
            category: StorageCategory,
            extension: string,
            displayNamePrefix: string,
            timestamp?: number,
            onDuplicate?: (existing: FileMetadata) => void
        ): Promise<string> => {
            // Skip if already a file reference (stable)
            if (isFileReference(dataUrl)) return dataUrl;
            
            // Skip if already a remote URL (legacy - keep for backward compatibility)
            if (!dataUrl.startsWith('data:') && !dataUrl.startsWith('blob:')) return dataUrl;
            
            let base64Data = dataUrl;
            if (dataUrl.startsWith('blob:')) {
                // The node that produced this media may already have uploaded it, or be
                // uploading it right now; a save never waits on that upload.
                const uploaded = resolveUploadedBlob(dataUrl);
                if (uploaded) return uploaded;
                if (isBlobUploading(dataUrl)) return dataUrl;
                base64Data = await blobUrlToBase64(dataUrl);
            }

            if (!base64Data.startsWith('data:')) return base64Data;

            try {
                // Use uploadWithDeduplication which checks content hash first
                // This prevents duplicate files when same content is uploaded multiple times
                const fileRef = await uploadWithDeduplication(userId, base64Data, {
                    category: category as FileRegistryCategory,
                    workspaceName,
                    displayNamePrefix,
                    extension,
                    nodeId,
                    ...(timestamp ? { timestamp } : {}),
                    ...(onDuplicate ? { onDuplicate } : {}),
                });
                if (dataUrl.startsWith('blob:') && isFileReference(fileRef)) finishBlobUpload(dataUrl, fileRef);
                // The upload did not get through (it hands the bytes back): the caller is told, so the node is sent again
                if (!isFileReference(fileRef)) unstored?.add(nodeId);
                                
                // Track upload if it wasn't a duplicate (file:// refs are returned either way)
                if (isFileReference(fileRef) && fileRef !== dataUrl) {
                    const fileSize = getBase64FileSize(base64Data);
                    totalBytesUploaded += fileSize;
                    filesUploaded++;
                }
                
                return fileRef;
                
            } catch (e: any) {
                console.warn(`Asset upload/register failed. Code: ${e.code}. Msg: ${e.message}. Keeping as base64.`);
                unstored?.add(nodeId);
                return base64Data;
            }
        };

        // A Reference node saved its media locally under a planned name; uploading under the same
        // name lets the app find the local copy instead of downloading this one.
        const isReferenceNode = nodeType === NodeType.MEDIA_REFERENCE || nodeType === NodeType.AUDIO_REFERENCE;
        const plannedName = (category: ReferenceStorageCategory) =>
            isReferenceNode ? parseReferenceStorageName(newData.localCopyName, category) : null;
        // The same bytes may already be in the cloud under another name; the local copy then takes that name.
        const moveLocalCopyTo = (category: ReferenceStorageCategory) => (existing: FileMetadata) => {
            const localName = newData.localCopyName;
            const parts = (existing.storagePath || '').split('/');
            if (!localName || parts.length < 2) return;
            const target = { category: parts[parts.length - 2], name: parts[parts.length - 1] };
            // Loaded on demand: localSave itself imports this module
            void import('./localSave')
                .then(({ moveLocalFile }) => moveLocalFile(workspaceName, { category, name: localName }, target, userId))
                .catch(() => undefined);
        };

        // Video upload (generated clips are mp4; a Reference node may hold mov or webm)
        if (newData.videoUri && (newData.videoUri.startsWith('blob:') || newData.videoUri.startsWith('data:'))) {
            const planned = plannedName('Videos');
            const videoExt = planned?.extension || videoExtensionFromMime(newData.videoMimeType || '', newData.videoFileName || '');
            newData.videoUri = await uploadAndRegisterAsset(newData.videoUri, 'Videos', videoExt, 'video', planned?.stamp, planned ? moveLocalCopyTo('Videos') : undefined);
        }

        // Audio reference upload (Audio node)
        if (newData.audioUrl && (newData.audioUrl.startsWith('blob:') || newData.audioUrl.startsWith('data:'))) {
            const planned = plannedName('Audio');
            const ext = planned?.extension || audioExtensionFromMime(newData.audioMimeType || '', newData.audioFileName || '');
            newData.audioUrl = await uploadAndRegisterAsset(newData.audioUrl, 'Audio', ext, 'audio', planned?.stamp, planned ? moveLocalCopyTo('Audio') : undefined);
        }

        // Uploaded/reference image (from ImageViewer, EntityNodes)
        if (newData.imageUrl && newData.imageUrl.startsWith('data:')) {
            const category = getStorageCategory(nodeType, nodeType === NodeType.SKETCH_PAD ? 'sketch' : 'uploaded');
            const prefix = nodeType === NodeType.SKETCH_PAD ? 'sketch' : 'image';
            const planned = category === 'Uploaded_Images' ? plannedName('Uploaded_Images') : null;
            newData.imageUrl = await uploadAndRegisterAsset(newData.imageUrl, category, planned?.extension || 'png', prefix, planned?.stamp, planned ? moveLocalCopyTo('Uploaded_Images') : undefined);
        }
        
        // Handle generatedImages array FIRST, then sync generatedImageUri
        if (newData.generatedImages && Array.isArray(newData.generatedImages)) {
            newData.generatedImages = await Promise.all(
                newData.generatedImages.map(async (imgUrl, index) => {
                    if (imgUrl.startsWith('data:')) {
                        return await uploadAndRegisterAsset(imgUrl, 'Generated_Images', 'png', `generated_${index}`);
                    }
                    return imgUrl;
                })
            );
            // Sync generatedImageUri to point to the first image in the array
            if (newData.generatedImages.length > 0) {
                newData.generatedImageUri = newData.generatedImages[0];
            }
        } else if (newData.generatedImageUri && newData.generatedImageUri.startsWith('data:')) {
            newData.generatedImageUri = await uploadAndRegisterAsset(newData.generatedImageUri, 'Generated_Images', 'png', 'generated');
        }
        
        // Handle referenceImages array (for entity nodes - Characters, Sets)
        if (newData.referenceImages && Array.isArray(newData.referenceImages)) {
            const category = getStorageCategory(nodeType, 'reference');
            const prefix = nodeType === NodeType.CHARACTER_NODE ? 'character' : 'reference';
            newData.referenceImages = await Promise.all(
                newData.referenceImages.map(async (imgUrl, index) => {
                    if (imgUrl.startsWith('data:')) {
                        return await uploadAndRegisterAsset(imgUrl, category, 'png', `${prefix}_${index}`);
                    }
                    return imgUrl;
                })
            );
        }

        // References an Image or Video node chose by address (before picks named a Reference node).
        // One that only this page held is stored like any upload; the node it came from stores the
        // same bytes, which the registry recognises, so nothing goes up twice. A pick that names a
        // Reference node holds no media of its own.
        if (Array.isArray(newData.pickedReferences)) {
            newData.pickedReferences = await Promise.all(newData.pickedReferences.map(async (reference) => {
                const url = reference.url;
                if (!url || !/^(data|blob):/i.test(url)) return reference;
                const category: StorageCategory = reference.kind === 'video' ? 'Videos' : reference.kind === 'audio' ? 'Audio' : 'Uploaded_Images';
                const extension = reference.kind === 'video' ? videoExtensionFromMime(reference.mimeType || '', reference.label)
                    : reference.kind === 'audio' ? audioExtensionFromMime(reference.mimeType || '', reference.label)
                    : 'png';
                return { ...reference, url: await uploadAndRegisterAsset(url, category, extension, 'reference') };
            }));
        }

        // Update storage usage if any files were uploaded
        if (totalBytesUploaded > 0) {
            await updateStorageUsage(userId, totalBytesUploaded, filesUploaded);
        }
    } else {
        // Local Mode: Convert blobs to base64 for storage (no file registry)
        if (newData.videoUri && newData.videoUri.startsWith('blob:')) {
            newData.videoUri = await blobUrlToBase64(newData.videoUri);
        }
        if (newData.generatedImageUri && newData.generatedImageUri.startsWith('blob:')) {
            newData.generatedImageUri = await blobUrlToBase64(newData.generatedImageUri);
        }
        if (newData.generatedImages && Array.isArray(newData.generatedImages)) {
            newData.generatedImages = await Promise.all(
                newData.generatedImages.map(async (imgUrl) => {
                    if (imgUrl.startsWith('blob:')) {
                        return await blobUrlToBase64(imgUrl);
                    }
                    return imgUrl;
                })
            );
        }
    }

    return newData;
};

// Export Helper
export const processNodesForExport = async (nodes: CanvasNode[]): Promise<CanvasNode[]> => {
    if (!nodes || !Array.isArray(nodes)) return [];
    
    return Promise.all(nodes.map(async node => {
        try {
            if (holdsLocalAddress(node)) await loadLocalAddressLookup();
            const newNode = withStoredReferences(JSON.parse(JSON.stringify(node)), 'drop');

            // Convert Blob URLs to Base64 for JSON persistence
            if (newNode.data.videoUri && newNode.data.videoUri.startsWith('blob:')) {
                 try {
                     newNode.data.videoUri = await blobUrlToBase64(newNode.data.videoUri);
                 } catch (e) {
                     console.error(`Failed to export video for node ${node.id}`, e);
                 }
            }
            
            if (newNode.data.generatedImageUri && newNode.data.generatedImageUri.startsWith('blob:')) {
                 try {
                     newNode.data.generatedImageUri = await blobUrlToBase64(newNode.data.generatedImageUri);
                 } catch (e) {
                     console.error(`Failed to export generated image for node ${node.id}`, e);
                 }
            }
            
            if (newNode.data.imageUrl && newNode.data.imageUrl.startsWith('blob:')) {
                 try {
                     newNode.data.imageUrl = await blobUrlToBase64(newNode.data.imageUrl);
                 } catch (e) {
                     console.error(`Failed to export image for node ${node.id}`, e);
                 }
            }
            
            // Handle generatedImages array
            if (newNode.data.generatedImages && Array.isArray(newNode.data.generatedImages)) {
                newNode.data.generatedImages = await Promise.all(
                    newNode.data.generatedImages.map(async (imgUrl: string) => {
                        if (imgUrl.startsWith('blob:')) {
                            try {
                                return await blobUrlToBase64(imgUrl);
                            } catch (e) {
                                console.error(`Failed to export generated image array item`, e);
                                return imgUrl;
                            }
                        }
                        return imgUrl;
                    })
                );
            }

            return newNode;
        } catch (e) {
            console.error("Export processing failed for node", node.id, e);
            return node; // Return original as fallback
        }
    }));
};

/**
 * A workspace's History as an exported file keeps it. Entries come in two shapes
 * (utils/historyItems.ts): a light one, which holds only file references and has no `data`,
 * and an old one holding a node's whole data, whose video may be one only this session holds
 * (a blob: address), written out inline so the file carries it.
 */
export const processHistoryForExport = async (history: (HistoryItem | LightHistoryItem)[]): Promise<(HistoryItem | LightHistoryItem)[]> =>
    Promise.all(history.map(async item => {
        const copy = JSON.parse(JSON.stringify(item));
        if (typeof copy.data?.videoUri === 'string' && copy.data.videoUri.startsWith('blob:')) {
            try {
                copy.data.videoUri = await blobUrlToBase64(copy.data.videoUri);
            } catch (e) {
                console.error('History blob export failed', e);
            }
        }
        return copy;
    }));

// --- WORKSPACE OPERATIONS ---

// Local-only project listing (used for fallback/merge)
const listLocalProjects = async (userId: string): Promise<ProjectMetadata[]> => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const projects: ProjectMetadata[] = [];
        
        const request = store.openCursor();
        request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result;
            if (cursor) {
                const val = cursor.value;
                if (val.ownerId === userId) {
                    projects.push({
                        id: cursor.key as string,
                        name: val.name || 'Untitled',
                        ownerId: val.ownerId,
                        lastSaved: val.lastSaved || 0,
                        createdAt: val.createdAt,
                        nodeCount: Array.isArray(val.nodes) ? val.nodes.length : val.nodeCount,
                        thumbnail: val.thumbnail || undefined,
                        folderId: val.folderId ?? null,
                        isFavorite: !!val.isFavorite,
                    });
                }
                cursor.continue();
            } else {
                resolve(projects.sort((a,b) => b.lastSaved - a.lastSaved));
            }
        };
        request.onerror = () => reject(request.error);
    });
};

export const listProjects = async (userId: string, cloudSyncEnabled: boolean = false): Promise<ProjectMetadata[]> => {
    // Validate auth
    requireAuth(userId, 'listProjects');
    logger.debug('Storage', `listProjects | userId: ${userId} | cloudSync: ${cloudSyncEnabled}`);
    
    let cloudProjects: ProjectMetadata[] = [];
    const cloudProjectIds = new Set<string>();

    // Thumbnails now live alongside the project doc in Firestore (set by saveProjectMetadata),
    // so no out-of-band lookup is needed.
    const thumbnailMap = new Map<string, string>();

    // Only query Firebase if cloud sync is enabled (Pro users)
    if (isFirebaseActive() && cloudSyncEnabled) {
        try {
            const db = getFirestore(firebaseApp!);
            // Fetch owned projects
            const q = query(collection(db, 'projects'), where("ownerId", "==", userId));
            const querySnapshot = await getDocs(q);
            cloudProjects = querySnapshot.docs.map(doc => {
                cloudProjectIds.add(doc.id);
                const d = doc.data();
                return {
                    id: doc.id,
                    name: d.name || 'Untitled Project',
                    ownerId: d.ownerId,
                    ...(d.ownerName ? { ownerName: d.ownerName } : {}),
                    lastSaved: d.lastSaved || 0,
                    createdAt: d.createdAt,
                    nodeCount: d.nodeCount,
                    isLocal: false,
                    thumbnail: d.thumbnail || thumbnailMap.get(doc.id),
                    folderId: d.folderId ?? null,
                    isFavorite: !!d.isFavorite,
                    totalCost: historyTotalCost(Array.isArray(d.history) ? d.history : []),
                };
            });
            console.log('[listProjects] Found', cloudProjects.length, 'cloud projects');

            // Fetch shared projects (where user is in sharedUserIds)
            try {
                const sharedQuery = query(
                    collection(db, 'projects'),
                    where("sharedUserIds", "array-contains", userId)
                );
                const sharedSnapshot = await getDocs(sharedQuery);
                const sharedProjects: ProjectMetadata[] = sharedSnapshot.docs
                    .filter(doc => !cloudProjectIds.has(doc.id))
                    .map(doc => {
                        const data = doc.data();
                        const sharedInfo = data.sharedWith?.[userId];
                        return {
                            id: doc.id,
                            name: data.name || 'Untitled Project',
                            ownerId: data.ownerId,
                            ...(data.ownerName ? { ownerName: data.ownerName } : {}),
                            lastSaved: data.lastSaved || 0,
                            createdAt: data.createdAt,
                            nodeCount: data.nodeCount,
                            isLocal: false,
                            isShared: true,
                            sharedPermission: sharedInfo?.permission || 'view',
                            // Who shared it, by name: the sharer's as the server wrote it, else the owner's
                            sharedBy: sharedInfo?.sharedByName || data.ownerName || undefined,
                            thumbnail: data.thumbnail || thumbnailMap.get(doc.id),
                            // Shared workspaces are organised by their owner, never filed by the recipient;
                            // one shared as part of a project stays in its place in that project
                            ...(typeof sharedInfo?.viaProject === 'string' ? { sharedProjectId: sharedInfo.viaProject } : {}),
                            folderId: typeof sharedInfo?.viaProject === 'string' ? (data.folderId ?? null) : null,
                            isFavorite: false,
                            totalCost: historyTotalCost(Array.isArray(data.history) ? data.history : []),
                        };
                    });
                cloudProjects = [...cloudProjects, ...sharedProjects];
                console.log('[listProjects] Found', sharedProjects.length, 'shared projects');
            } catch (e) {
                console.warn('[listProjects] Failed to fetch shared projects:', e);
            }
        } catch (e) {
            console.warn("Cloud listProjects failed (permissions?), merging with local.", e);
        }
    } else {
        console.log('[listProjects] Skipping cloud query - firebaseActive:', isFirebaseActive(), 'cloudSyncEnabled:', cloudSyncEnabled);
    }

    const localProjects = await listLocalProjects(userId);
    console.log('[listProjects] Found', localProjects.length, 'local projects');
    
    // Mark local projects that aren't in the cloud
    const localProjectsWithFlag = localProjects.map(p => ({
        ...p,
        isLocal: !cloudProjectIds.has(p.id), // Local-only if not in cloud
        thumbnail: thumbnailMap.get(p.id) || p.thumbnail
    }));

    // Merge cloud + local (prefer newest by lastSaved for same id, but keep isLocal flag from local if exists in both)
    const mergedMap = new Map<string, ProjectMetadata>();
    
    // Add cloud projects first
    cloudProjects.forEach((p) => {
        mergedMap.set(p.id, p);
    });
    
    // Then add/update with local projects
    localProjectsWithFlag.forEach((p) => {
        const existing = mergedMap.get(p.id);
        if (!existing) {
            // Local only - not in cloud
            mergedMap.set(p.id, { ...p, isLocal: true });
        } else if ((p.lastSaved || 0) > (existing.lastSaved || 0)) {
            // Exists in both, local is newer - keep cloud status but update data.
            // Organisation metadata (folder, star) is written cloud-first, so the cloud
            // row is its source of truth even when the mirror's canvas save is newer.
            mergedMap.set(p.id, {
                ...existing,
                ...p,
                isLocal: false,
                // Whose workspace it is, is for the cloud to say: this device's copy names whoever
                // saved it here, which for a shared workspace is not its owner
                ownerId: existing.ownerId ?? p.ownerId,
                thumbnail: p.thumbnail || existing.thumbnail,
                createdAt: p.createdAt ?? existing.createdAt,
                folderId: existing.folderId ?? null,
                isFavorite: !!existing.isFavorite,
            });
        }
        // If cloud is newer, keep cloud version (already in map)
    });

    return Array.from(mergedMap.values()).sort((a, b) => (b.lastSaved || 0) - (a.lastSaved || 0));
};

export const createProject = async (userId: string, name: string, cloudSyncEnabled: boolean = false, folderId: string | null = null): Promise<string> => {
    // Validate auth
    requireAuth(userId, 'createProject');
    logger.debug('Storage', `createProject | userId: ${userId} | name: ${name} | cloudSync: ${cloudSyncEnabled} | folder: ${folderId ?? 'none'}`);
    const createdAt = Date.now();
    
    // Generate secure random ID if possible, else fallback
    let projectId;
    try {
        projectId = crypto.randomUUID();
    } catch (e) {
        projectId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }
    
    // Default initial node
    const initialNode: CanvasNode = {
        id: '1',
        type: NodeType.MEDIA_REFERENCE,
        position: { x: 100, y: 100 },
        size: { width: 320, height: 300 },
        data: { title: 'Reference', timestamp: Date.now() },
    };

    // Only save to Firebase if cloud sync is enabled
    console.log('[createProject] firebaseActive:', isFirebaseActive(), 'cloudSyncEnabled:', cloudSyncEnabled);
    if (isFirebaseActive() && cloudSyncEnabled) {
        console.log('[createProject] Attempting cloud save to Firestore...');
        try {
            const db = getFirestore(firebaseApp!);
            const batch = writeBatch(db);

            // Write project metadata (v2 schema — nodes in sub-collection)
            batch.set(doc(db, 'projects', projectId), {
                name,
                ownerId: userId,
                ...(currentUserName() ? { ownerName: currentUserName() } : {}),
                connections: [],
                history: [],
                transform: { x: 0, y: 0, scale: 1 },
                lastSaved: createdAt,
                createdAt,
                folderId,
                schemaVersion: 2 as ProjectSchemaVersion,
            });

            // Write initial node as sub-document
            batch.set(doc(db, 'projects', projectId, 'nodes', initialNode.id), initialNode);

            await batch.commit();
            console.log('[createProject] ✅ Cloud save successful, projectId:', projectId);
            
            // Also save to local IndexedDB for offline access
            const initialData = {
                name,
                ownerId: userId,
                nodes: [initialNode],
                connections: [],
                history: [],
                transform: { x: 0, y: 0, scale: 1 },
                lastSaved: createdAt,
                createdAt,
                folderId,
            };
            try {
                const localDb = await openDB();
                await new Promise<void>((resolve, reject) => {
                    const transaction = localDb.transaction([STORE_NAME], 'readwrite');
                    const store = transaction.objectStore(STORE_NAME);
                    const request = store.put(initialData, projectId);
                    request.onsuccess = () => resolve();
                    request.onerror = () => reject(request.error);
                });
                logger.debug('Storage', `createProject: Also saved to local IndexedDB`);
            } catch (localErr) {
                console.warn('createProject: Local backup save failed:', localErr);
            }
            
            return projectId;
        } catch (e) {
            console.warn("Cloud createProject failed, falling back to local.", e);
            logger.debug('Storage', `createProject: Cloud save FAILED, falling back to local`);
        }
    } else {
        logger.debug('Storage', `createProject: Skipping cloud (firebaseActive=${isFirebaseActive()}, cloudSyncEnabled=${cloudSyncEnabled})`);
    }

    // Save locally for free users or as fallback (still v1 format for IndexedDB)
    const initialData = {
        name,
        ownerId: userId,
        nodes: [initialNode],
        connections: [],
        history: [],
        transform: { x: 0, y: 0, scale: 1 },
        lastSaved: createdAt,
        createdAt,
        folderId,
    };

    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(initialData, projectId);
        request.onsuccess = () => resolve(projectId);
        request.onerror = () => reject(request.error);
    });
};

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
const removeUndefined = <T extends object>(obj: T): Partial<T> => {
    return Object.fromEntries(
        Object.entries(obj).filter(([_, value]) => value !== undefined)
    ) as Partial<T>;
};

/** Stands for a value that is not written: the cloud keeps what it has under that key (the writes are merges). */
const LEFT_OUT = Symbol('left out');

/**
 * Recursively strip values that Firestore cannot store:
 * - undefined  → removed
 * - base64 data URIs (data:...) → replaced with '' (too large for Firestore 1MB limit)
 * - blob: URLs → replaced with '' (browser-local, useless across devices)
 * - the desktop app's address of a local file → the file reference it stands for. When that
 *   is not known its key is left out, so the cloud keeps the reference it has; a list is
 *   written whole, so one such entry leaves the cloud's list as it is.
 * Safe to call on deeply nested node data objects.
 */
const sanitizeForFirestore = (value: any, inList = false): any => {
    if (value === undefined) return null;
    if (typeof value === 'string') {
        if (value.startsWith('data:')) return ''; // base64 — strip it
        if (value.startsWith('blob:')) return ''; // blob URL — strip it
        if (isLocalMediaUrl(value)) return referenceOfLocalAddress(value) ?? LEFT_OUT;
        return value;
    }
    if (Array.isArray(value)) {
        const out = value.map(item => sanitizeForFirestore(item, true));
        return out.includes(LEFT_OUT) ? LEFT_OUT : out;
    }
    if (value !== null && typeof value === 'object') {
        const out: Record<string, any> = {};
        for (const [k, v] of Object.entries(value)) {
            if (v === undefined) continue; // remove undefined keys
            const kept = sanitizeForFirestore(v, inList);
            if (kept === LEFT_OUT) {
                if (inList) return LEFT_OUT;
                continue;
            }
            out[k] = kept;
        }
        return out;
    }
    return value;
};

/**
 * The payload of a node document with every key the canvas cleared (set to undefined) marked
 * for deletion. The documents are written as merges, so that a key left out keeps its value in
 * the cloud (see sanitizeForFirestore); a key that was cleared on purpose would be kept the
 * same way and come back at the next open. `live` is the canvas's own object, which still
 * holds the undefined keys.
 */
const withClearedKeys = (payload: Record<string, any>, live: unknown): Record<string, any> => {
    if (!isPlainObject(live) || !isPlainObject(payload)) return payload;
    for (const [key, value] of Object.entries(live)) {
        if (value === undefined) payload[key] = deleteField();
        else if (isPlainObject(value) && isPlainObject(payload[key])) withClearedKeys(payload[key] as Record<string, any>, value);
    }
    return payload;
};

/**
 * Sanitize history for saving - converts any format to lightweight format
 * Strips out base64 data, keeps only file:// references and metadata
 * This ensures history doesn't bloat the project document
 * NOTE: Firestore doesn't accept undefined values, so we filter them out
 */
/**
 * History as it is saved: light entries only. Title, prompt and cost are read from the entry
 * and then from an old entry's `data`, so an entry whose picture was never stored keeps them.
 */
export const sanitizeHistoryForSave = (history: (HistoryItem | LightHistoryItem)[]): Partial<LightHistoryItem>[] => {
    // Keep file:// refs; a blob URL that was uploaded this session becomes its file
    // reference; anything else (base64, an upload still pending) is dropped
    // The desktop app's address of a local file becomes the reference it stands for
    const keepRef = (ref?: string) => (isFileReference(ref) ? ref : resolveUploadedBlob(ref) ?? (isLocalMediaUrl(ref) ? referenceOfLocalAddress(ref as string) : undefined));
    const firstKept = (...refs: (string | undefined)[]) => refs.map(keepRef).find(Boolean);
    return history.map(item => {
        const light = item as Partial<LightHistoryItem>;
        const data: Partial<HistoryItem['data']> = 'data' in item && item.data ? item.data : {};
        const { title, prompt, cost } = historyItemFields(item);
        return removeUndefined({
            id: item.id,
            type: item.type,
            timestamp: item.timestamp,
            title,
            prompt,
            cost,
            imageRef: firstKept(light.imageRef, data.generatedImageUri, data.imageUrl),
            videoRef: firstKept(light.videoRef, data.videoUri),
        });
    });
};

export interface SaveProjectOptions {
    /** The cloud already holds this very history (it was loaded from there, or saved there, and has not changed since): it is not sent again. */
    historyUnchanged?: boolean;
    /**
     * The connections were not changed here, and the cloud's may be newer (the canvas holds a
     * copy read from this device, and the cloud has not been heard yet): the cloud keeps the
     * ones it has.
     */
    connectionsUnchanged?: boolean;
    /**
     * This save carries no change of the workspace (a save on leaving with nothing unsaved:
     * the view and the thumbnail only). Should the cloud not take it, this device's copy keeps
     * the date it had: that date says when the copy last held work of its own, and a copy
     * dated after the cloud's last save is taken for the newer one when it is opened next.
     */
    keepLocalDate?: boolean;
}

export interface SaveProjectResult {
    /**
     * 'saved': the cloud has it. 'failed': the cloud save was tried and did not get through
     * (the copy on this device was still written). 'off': no cloud save was tried (no cloud
     * sync, or signed out).
     */
    cloud: 'saved' | 'failed' | 'off';
    /** With 'saved': the time written on every node document of this save (lastModifiedAt), which tells this session's own writes from others' when they come back. */
    stamp: number;
    /**
     * With 'saved': the nodes that were written without their media, because its upload failed
     * (the cloud holds an empty string in its place). They are not saved yet: the caller keeps
     * them as changed, so a later save uploads again.
     */
    unstored?: string[];
}

/** Only some nodes were to be written, but the cloud does not have this workspace's nodes as documents yet. */
class NodeDocumentsMissing extends Error {}

export const saveProject = async (
  nodes: CanvasNode[],
  connections: Connection[],
  history: (HistoryItem | LightHistoryItem)[],
  transform: ViewportTransform,
  userId: string,
  projectId: string,
  projectName: string,
  cloudSyncEnabled: boolean = false,
  thumbnail?: string,
  // If provided, only these nodes are written (an empty set writes none: only the workspace's
  // details, connections, history and thumbnail); otherwise every node, and removed ones are cleared
  dirtyNodeIds?: Set<string> | null,
  options: SaveProjectOptions = {},
): Promise<SaveProjectResult> => {
    // Validate auth and required params
    requireAuth(userId, 'saveProject');
    validateParams({ projectId }, 'saveProject');

    logger.debug('Storage', `saveProject | userId: ${userId} | projectId: ${projectId} | name: ${projectName} | cloudSync: ${cloudSyncEnabled} | dirty: ${dirtyNodeIds?.size ?? 'all'} | nodeCount: ${nodes.length}`);

    // A local address a view left in the data is saved as the file reference it stands for
    if (holdsLocalAddress(nodes) || holdsLocalAddress(history)) await loadLocalAddressLookup();

    // Prepare data (uploads assets to cloud if active and permitted, otherwise keeps base64)
    let processedNodes = nodes;
    let processedHistory: Partial<LightHistoryItem>[] = [];
    const unstored = new Set<string>();
    // The canvas's own objects: they still say which keys were cleared (set to undefined), which a copy does not
    const liveById = new Map(nodes.map(n => [n.id, n]));

    try {
        // Only process dirty nodes when doing incremental save, otherwise all
        const nodesToProcess = dirtyNodeIds
            ? nodes.filter(n => dirtyNodeIds.has(n.id))
            : nodes;

        const processedDirty = await Promise.all(nodesToProcess.map(async (n) => {
            const node = JSON.parse(JSON.stringify(n));
            node.data = await prepareDataForStorage(node.data, userId, node.id, n.type, projectName, unstored);
            return node;
        }));

        if (dirtyNodeIds) {
            // Merge processed dirty nodes back into the full list
            const processedMap = new Map(processedDirty.map(n => [n.id, n]));
            processedNodes = nodes.map(n => processedMap.get(n.id) || n);
        } else {
            processedNodes = processedDirty;
        }

        processedHistory = sanitizeHistoryForSave(history);
    } catch (prepError) {
        console.error("Data preparation failed, attempting raw save:", prepError);
        processedNodes = nodes;
        processedHistory = sanitizeHistoryForSave(history);
    }

    // Project metadata (incl. thumbnail) is persisted inside the Firestore
    // projects/{id} document itself, so no separate metadata write is needed.

    // Save to Firebase if cloud sync is enabled AND user is authenticated
    const _auth = getAuth(firebaseApp!);
    const firebaseActive = isFirebaseActive();
    const hasCurrentUser = !!_auth.currentUser;
    
    console.log('[saveProject] cloud check - firebaseActive:', firebaseActive, 'cloudSyncEnabled:', cloudSyncEnabled, 'hasUser:', hasCurrentUser, 'userId:', userId);
    
    const result: SaveProjectResult = { cloud: 'off', stamp: 0 };

    if (firebaseActive && cloudSyncEnabled && hasCurrentUser) {
        try {
            // Always refresh the auth token before writing (avoids stale-token 401 errors)
            await _auth.currentUser!.getIdToken();
        } catch (tokenErr) {
            console.warn('[saveProject] Token refresh failed, proceeding anyway:', tokenErr);
        }
        try {
            const db = getFirestore(firebaseApp!);
            const now = Date.now();
            const projectRef = doc(db, 'projects', projectId);

            // Use a transaction for optimistic locking with a version counter.
            // The version field is atomically incremented on each save.
            // If two sessions try to save simultaneously, both succeed sequentially
            // because Firestore transactions retry on contention. Combined with
            // sessionId echo suppression, the other session will see the updated
            // version and data via its onSnapshot listener.
            await runTransaction(db, async (transaction) => {
                const projectSnap = await transaction.get(projectRef);
                // Writing only the changed nodes needs the others to be in the cloud as node
                // documents already. A workspace the cloud has never seen has none, and one saved
                // before node documents existed keeps its nodes inside the project document.
                if (dirtyNodeIds && (!projectSnap.exists() || projectSnap.data().schemaVersion !== 2)) {
                    throw new NodeDocumentsMissing();
                }
                const currentVersion = projectSnap.exists() ? (projectSnap.data().version || 0) : 0;
                const newVersion = currentVersion + 1;

                // Build project metadata — only set ownerId on create to
                // prevent shared users from accidentally claiming ownership.
                const projectData: Record<string, any> = {
                    name: projectName,
                    transform,
                    lastSaved: now,
                    schemaVersion: 2 as ProjectSchemaVersion,
                    nodeCount: processedNodes.length,
                    lastEditedBySession: getSyncSessionId(),
                    version: newVersion,
                };
                // The cloud keeps the connections it has (the write is a merge) when they were
                // not changed here and the cloud's may be newer
                if (!options.connectionsUnchanged || !projectSnap.exists()) {
                    projectData.connections = connections;
                }
                // The history is the bulk of the document: the cloud keeps the one it has
                // (the write is a merge) when the caller knows it is the same
                if (!options.historyUnchanged || !projectSnap.exists()) {
                    projectData.history = processedHistory;
                }
                if (!projectSnap.exists()) {
                    projectData.ownerId = userId;
                }
                // The owner's name goes with the record, so the people it is shared with can say whose it is
                const ownerName = currentUserName();
                if (ownerName && (!projectSnap.exists() || projectSnap.data().ownerId === userId) && projectSnap.data()?.ownerName !== ownerName) {
                    projectData.ownerName = ownerName;
                }

                // The thumbnail is a small (<=30KB) JPEG data URL; sanitizeForFirestore would
                // strip it as a data URI, so it is merged in afterwards on purpose.
                transaction.set(
                    projectRef,
                    { ...sanitizeForFirestore(projectData), ...(thumbnail ? { thumbnail } : {}) },
                    { merge: true }
                );

                if (dirtyNodeIds) {
                    // Incremental save: only write dirty nodes (none for an empty set)
                    for (const node of processedNodes) {
                        if (dirtyNodeIds.has(node.id)) {
                            transaction.set(
                                doc(db, 'projects', projectId, 'nodes', node.id),
                                withClearedKeys(sanitizeForFirestore({ ...node, lastModifiedAt: now }), liveById.get(node.id)),
                                { merge: true }
                            );
                        }
                    }
                } else {
                    // Full save: write all nodes
                    for (const node of processedNodes) {
                        transaction.set(
                            doc(db, 'projects', projectId, 'nodes', node.id),
                            withClearedKeys(sanitizeForFirestore({ ...node, lastModifiedAt: now }), liveById.get(node.id)),
                            { merge: true }
                        );
                    }
                }
            });

            // Clean up orphan node sub-documents on full save
            // (nodes deleted from canvas but whose sub-docs weren't removed)
            if (!dirtyNodeIds) {
                try {
                    const nodesCol = collection(db, 'projects', projectId, 'nodes');
                    const existingDocs = await getDocs(nodesCol);
                    const currentNodeIds = new Set(processedNodes.map(n => n.id));
                    const orphanDeletes: Promise<void>[] = [];
                    for (const docSnap of existingDocs.docs) {
                        if (!currentNodeIds.has(docSnap.id)) {
                            orphanDeletes.push(deleteDoc(doc(db, 'projects', projectId, 'nodes', docSnap.id)));
                            logger.debug('Storage', `Deleting orphan node sub-doc: ${docSnap.id}`);
                        }
                    }
                    if (orphanDeletes.length > 0) {
                        await Promise.all(orphanDeletes);
                        logger.info('Storage', `Cleaned up ${orphanDeletes.length} orphan node sub-docs`);
                    }
                } catch (cleanupErr) {
                    console.warn('Orphan node cleanup failed (non-fatal):', cleanupErr);
                }
            }

            markFirestoreWriteOk();
            result.cloud = 'saved';
            result.stamp = now;
            noteWorkspaceSaved(projectId, now);
            if (unstored.size > 0) result.unstored = [...unstored];
            console.log(`[saveProject] ✅ Cloud save SUCCESS for ${projectId} (v2) with ${dirtyNodeIds?.size ?? processedNodes.length} node writes`);
            // Don't return — always save to local too as backup
        } catch (e: any) {
            if (e instanceof NodeDocumentsMissing) {
                logger.info('Storage', `saveProject: ${projectId} has no node documents in the cloud yet, saving every node`);
                return saveProject(nodes, connections, history, transform, userId, projectId, projectName, cloudSyncEnabled, thumbnail, null, options);
            }
            result.cloud = 'failed';
            console.error(`[saveProject] ❌ Cloud save FAILED. Code: ${e.code}. Msg: ${e.message}. Will still save locally.`);
        }
    }

    // ALWAYS save to local IndexedDB (as backup, or primary for free users)
    const now = Date.now();
    const projectData = {
        name: projectName,
        ownerId: userId,
        // Nodes that were not written this time are kept as the canvas has them
        nodes: withStoredReferences(processedNodes, 'drop'),
        connections,
        history: processedHistory,
        transform,
        lastSaved: now
    };

    try {
        const localDb = await openDB();
        await new Promise<void>((resolve, reject) => {
            const transaction = localDb.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            // Read-modify-write so organisation metadata (folder, star, thumbnail,
            // createdAt) survives a full canvas save.
            const getRequest = store.get(projectId);
            getRequest.onsuccess = () => {
                const existing = (getRequest.result || {}) as Record<string, any>;
                // Nodes this save did not process are as the canvas has them (see withoutSessionBlobs)
                const before = new Map<string, CanvasNode>((Array.isArray(existing.nodes) ? existing.nodes : []).map((n: CanvasNode) => [n.id, n]));
                const storedNodes = dirtyNodeIds
                    ? projectData.nodes.map(n => (dirtyNodeIds.has(n.id) ? n : withoutSessionBlobs(n, before.get(n.id))))
                    : projectData.nodes;
                const record = {
                    ...projectData,
                    nodes: storedNodes,
                    // A save that carried no change and that the cloud did not take leaves the copy's date alone
                    // (see keepLocalDate). A copy made by such a save has no date at all: dated today, it would
                    // later pass for newer than what another device saved before it
                    lastSaved: options.keepLocalDate && result.cloud === 'failed' ? (typeof existing.lastSaved === 'number' ? existing.lastSaved : 0) : now,
                    createdAt: existing.createdAt ?? now,
                    folderId: existing.folderId ?? null,
                    isFavorite: !!existing.isFavorite,
                    thumbnail: thumbnail || existing.thumbnail,
                };
                const request = store.put(record, projectId);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            };
            getRequest.onerror = () => reject(getRequest.error);
        });
        console.log(`[saveProject] ✅ Local save SUCCESS for ${projectId}`);
    } catch (localErr) {
        console.error('[saveProject] ❌ Local save FAILED:', localErr);
    }
    return result;
};

export const loadProject = async (projectId: string, cloudSyncEnabled: boolean = false): Promise<{
  nodes: CanvasNode[];
  connections: Connection[];
  history: HistoryItem[];
  transform: ViewportTransform;
  name?: string;
  version?: number;
  lastSaved?: number;
  /** Where it was read: 'cloud', or 'local' for this device's copy (no cloud sync, or the cloud could not be read). */
  source: 'cloud' | 'local';
} | null> => {
    if (!projectId) return null;
    
    logger.debug('Storage', `loadProject called: projectId=${projectId}, cloudSync=${cloudSyncEnabled}`);

    let data: any = null;
    let nodesFromSubCollection: CanvasNode[] | null = null;

    // Only try Firebase if cloud sync is enabled
    if (isFirebaseActive() && cloudSyncEnabled) {
         // Ensure Firebase Auth token is ready before Firestore operations
         const auth = getAuth(firebaseApp!);
         let hasValidAuth = !!auth.currentUser;
         if (hasValidAuth) {
             try { await auth.currentUser!.getIdToken(); } catch (_) { hasValidAuth = false; }
         }
         
         logger.debug('Storage', `loadProject auth check: hasValidAuth=${hasValidAuth}, user=${auth.currentUser?.uid}`);
         
         if (!hasValidAuth) {
             console.warn('loadProject: No authenticated user, skipping cloud load');
         } else {
             try {
                 const db = getFirestore(firebaseApp!);
                 const docSnap = await getDoc(doc(db, 'projects', projectId));
                 logger.debug('Storage', `loadProject docSnap exists: ${docSnap.exists()}`);
                 if (docSnap.exists()) {
                     data = docSnap.data();
                     logger.debug('Storage', `loadProject schemaVersion: ${data.schemaVersion}, nodeCount: ${data.nodeCount}`);
                     
                     // v2 schema: load nodes from sub-collection
                     if (data.schemaVersion === 2) {
                         const nodesSnapshot = await getDocs(collection(db, 'projects', projectId, 'nodes'));
                         // Answered from this client's cache: the connection dropped after the project document was read
                         if (nodesSnapshot.metadata?.fromCache) throw new Error('The node documents could not be read from the cloud');
                         nodesFromSubCollection = nodesSnapshot.docs.map(d => d.data() as CanvasNode);
                         logger.debug('Storage', `Loaded ${nodesFromSubCollection.length} nodes from sub-collection (v2)`);
                     } else {
                         logger.debug('Storage', `loadProject v1 schema, inline nodes: ${data.nodes?.length || 0}`);
                     }
                 }
             } catch (e) {
                 console.warn("Cloud loadProject failed, checking local.", e);
                 // Half a workspace is none: this device's copy is used, and reported as such
                 data = null;
                 nodesFromSubCollection = null;
             }
         }
    }
    
    // Load from local for free users or as fallback
    const source = data ? 'cloud' : 'local';
    if (!data) {
        const localDb = await openDB();
        data = await new Promise((resolve, reject) => {
            const transaction = localDb.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(projectId);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    if (!data) {
        logger.debug('Storage', 'loadProject: No data found, returning null');
        return null;
    }

    // Use sub-collection nodes if available (v2), otherwise inline nodes (v1)
    const rawNodes = nodesFromSubCollection || data.nodes || [];
    logger.debug('Storage', `loadProject: rawNodes count = ${rawNodes.length}`);
    const rehydratedNodes = await rehydrateNodes(rawNodes);
    logger.debug('Storage', `loadProject: rehydratedNodes count = ${rehydratedNodes.length}`);
    
    // We also need to rehydrate history items if they contain media
    // Note: rehydrateNodes works on CanvasNode, HistoryItem is slightly different shape but mostly NodeData
    // For simplicity, we manually rehydrate history here
    const rehydratedHistory = await Promise.all((data.history || []).map(async (item: HistoryItem) => {
        // Light entries have no `data`; giving them one hid their cost
        if (!item.data) return item;
        try {
            const newItem = { ...item, data: { ...item.data } };
            if (newItem.data.videoUri) {
                newItem.data.videoUri = await base64ToBlobUrl(newItem.data.videoUri);
            }
            return newItem;
        } catch (e) {
            return item;
        }
    }));

    logger.debug('Storage', `loadProject returning: ${rehydratedNodes.length} nodes, ${(data.connections || []).length} connections`);
    return { 
        nodes: rehydratedNodes, 
        connections: data.connections || [], 
        history: rehydratedHistory, 
        transform: data.transform || { x: 0, y: 0, scale: 1 },
        name: data.name,
        version: data.version || 0,
        lastSaved: data.lastSaved || 0,
        source,
    };
};

/**
 * Only the Reference nodes of a workspace that is not open, for the reference lists of the
 * other workspaces in its project. Nothing is rehydrated and no media is loaded. A v2 cloud
 * workspace is asked for just those node documents; a v1 one has its nodes inline, and a
 * workspace without cloud sync is read from this browser's copy, as `loadProject` does.
 */
export const loadProjectReferenceNodes = async (projectId: string, cloudSyncEnabled: boolean): Promise<CanvasNode[]> => {
    if (!projectId) return [];
    const referenceTypes: string[] = [NodeType.MEDIA_REFERENCE, NodeType.AUDIO_REFERENCE];
    const onlyReferences = (nodes: unknown): CanvasNode[] =>
        (Array.isArray(nodes) ? nodes : []).filter((node): node is CanvasNode => !!node && referenceTypes.includes((node as CanvasNode).type));

    if (isFirebaseActive() && cloudSyncEnabled && getAuth(firebaseApp!).currentUser) {
        try {
            const db = getFirestore(firebaseApp!);
            const snap = await getDoc(doc(db, 'projects', projectId));
            if (snap.exists()) {
                const data = snap.data();
                if (data.schemaVersion === 2) {
                    const nodes = await getDocs(query(collection(db, 'projects', projectId, 'nodes'), where('type', 'in', referenceTypes)));
                    return nodes.docs.map(d => d.data() as CanvasNode);
                }
                return onlyReferences(data.nodes);
            }
        } catch (e) {
            console.warn('[projectReferences] Cloud read failed, using this browser\'s copy:', e);
        }
    }
    const localDb = await openDB();
    const data = await new Promise<any>((resolve, reject) => {
        const request = localDb.transaction([STORE_NAME], 'readonly').objectStore(STORE_NAME).get(projectId);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
    return onlyReferences(data?.nodes);
};

/**
 * Get lightweight cloud project metadata (version + lastSaved) for freshness checks.
 * Does NOT load nodes or full data — just reads the project document.
 */
export const getCloudProjectVersion = async (projectId: string): Promise<{ version: number; lastSaved: number } | null> => {
    if (!isFirebaseActive()) return null;
    const auth = getAuth(firebaseApp!);
    if (!auth.currentUser) return null;
    
    try {
        const db = getFirestore(firebaseApp!);
        const docSnap = await getDoc(doc(db, 'projects', projectId));
        if (!docSnap.exists()) return null;
        const data = docSnap.data();
        return {
            version: data.version || 0,
            lastSaved: data.lastSaved || 0,
        };
    } catch (e) {
        console.warn('getCloudProjectVersion failed:', e);
        return null;
    }
};

export const deleteProject = async (projectId: string, cloudSyncEnabled: boolean = false): Promise<void> => {
    // Validate params
    validateParams({ projectId }, 'deleteProject');
    logger.debug('Storage', `deleteProject | projectId: ${projectId} | cloudSync: ${cloudSyncEnabled}`);
    
    // Only delete from Firebase if cloud sync is enabled
    if (isFirebaseActive() && cloudSyncEnabled) {
        try {
            const db = getFirestore(firebaseApp!);
            
            // Delete all node sub-documents first (v2 schema)
            try {
                const nodesSnapshot = await getDocs(collection(db, 'projects', projectId, 'nodes'));
                if (nodesSnapshot.size > 0) {
                    const batch = writeBatch(db);
                    nodesSnapshot.docs.forEach(nodeDoc => batch.delete(nodeDoc.ref));
                    await batch.commit();
                    logger.debug('Storage', `Deleted ${nodesSnapshot.size} node sub-documents`);
                }
            } catch (e) {
                console.warn('Failed to delete node sub-documents:', e);
            }
            
            // Delete the project document
            await withCloudTimeout(() => deleteDoc(doc(db, 'projects', projectId)));
            // Fall through: the IndexedDB mirror row must go too, otherwise listProjects
            // (and the sign-in local→cloud sync) resurrects the workspace as "local-only".
        } catch (e) {
            console.error('[deleteProject] Cloud delete failed:', e);
            throw new Error(describeCloudError(e, 'Could not delete the workspace'));
        }
    }

    // Delete from local (primary for free users, mirror cleanup otherwise)
    const localDb = await openDB();
    await new Promise<void>((resolve, reject) => {
        const transaction = localDb.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const req = store.delete(projectId);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
};

// --- PER-NODE OPERATIONS (v2 schema) ---

/**
 * Save a single node to its sub-document.
 * Used for incremental saves when only one node changes.
 */
export const saveNodeToCloud = async (
    projectId: string,
    node: CanvasNode,
    userId: string,
    projectName: string
): Promise<void> => {
    if (!isFirebaseActive()) return;
    
    try {
        const db = getFirestore(firebaseApp!);
        // Prepare node data for storage (upload assets etc.)
        const processedNode = JSON.parse(JSON.stringify(node));
        processedNode.data = await prepareDataForStorage(processedNode.data, userId, node.id, node.type, projectName);
        
        await setDoc(
            doc(db, 'projects', projectId, 'nodes', node.id),
            { ...processedNode, lastModifiedAt: Date.now() },
            { merge: true }
        );
    } catch (e) {
        logger.error('Storage', `Failed to save node ${node.id} to cloud:`, e);
        throw e;
    }
};

/**
 * Delete a single node sub-document from Firestore.
 */
export const deleteNodeFromCloud = async (
    projectId: string,
    nodeId: string
): Promise<void> => {
    if (!isFirebaseActive()) return;
    if (isFirestoreWriteAvailable() === false) return; // Silently skip — writes are denied
    
    try {
        const db = getFirestore(firebaseApp!);
        await deleteDoc(doc(db, 'projects', projectId, 'nodes', nodeId));
        markFirestoreWriteOk();
        logger.debug('Storage', `Deleted node ${nodeId} from cloud`);
    } catch (e: any) {
        if (e.code === 'permission-denied') {
            markFirestoreWriteDenied();
            return; // Silently skip — don't throw
        }
        logger.error('Storage', `Failed to delete node ${nodeId} from cloud:`, e);
        throw e;
    }
};

/**
 * Migrate a v1 project (inline nodes) to v2 (sub-document nodes).
 * Reads the existing document, writes each node to a sub-document,
 * removes the inline nodes array, and sets schemaVersion: 2.
 */
export const migrateProjectToV2 = async (projectId: string): Promise<boolean> => {
    if (!isFirebaseActive()) return false;
    
    try {
        const db = getFirestore(firebaseApp!);
        const projectRef = doc(db, 'projects', projectId);
        const projectSnap = await getDoc(projectRef);
        
        if (!projectSnap.exists()) return false;
        
        const data = projectSnap.data();
        if (data.schemaVersion === 2) {
            logger.debug('Storage', `Project ${projectId} already v2`);
            return true;
        }
        
        const inlineNodes: CanvasNode[] = data.nodes || [];
        if (inlineNodes.length === 0) {
            // No nodes to migrate, just set version
            await setDoc(projectRef, { schemaVersion: 2, nodes: [] }, { merge: true });
            return true;
        }
        
        logger.info('Storage', `Migrating project ${projectId}: ${inlineNodes.length} nodes to sub-documents`);
        
        // Write nodes to sub-collection in batches (Firestore limit: 500 ops per batch)
        const batchSize = 450;
        for (let i = 0; i < inlineNodes.length; i += batchSize) {
            const batch = writeBatch(db);
            const chunk = inlineNodes.slice(i, i + batchSize);
            
            for (const node of chunk) {
                batch.set(doc(db, 'projects', projectId, 'nodes', node.id), {
                    ...node,
                    lastModifiedAt: Date.now(),
                });
            }
            
            await batch.commit();
        }
        
        // Update parent document: remove inline nodes, set v2
        // We use setDoc with merge to keep all other fields but overwrite nodes to empty
        await setDoc(projectRef, {
            schemaVersion: 2 as ProjectSchemaVersion,
            nodeCount: inlineNodes.length,
            // Clear the inline nodes array to free document space
            nodes: [],
        }, { merge: true });
        
        logger.info('Storage', `Migration complete for project ${projectId}`);
        return true;
    } catch (e) {
        logger.error('Storage', `Migration failed for project ${projectId}:`, e);
        return false;
    }
};

/**
 * Upload a local-only project to the cloud (Firebase)
 * This copies the project from IndexedDB to Firestore
 */
export const uploadProjectToCloud = async (projectId: string, userId: string): Promise<boolean> => {
    // Validate auth and params
    requireAuth(userId, 'uploadProjectToCloud');
    validateParams({ projectId }, 'uploadProjectToCloud');
    
    if (!isFirebaseActive()) {
        console.error("[uploadProjectToCloud] Firebase not active, cannot upload to cloud");
        return false;
    }
    
    // Check auth
    const auth = getAuth(firebaseApp!);
    if (!auth.currentUser) {
        console.error("[uploadProjectToCloud] No authenticated user");
        return false;
    }

    try {
        // First, load the project from local storage
        const localDb = await openDB();
        const localData: any = await new Promise((resolve, reject) => {
            const transaction = localDb.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(projectId);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });

        if (!localData) {
            console.error(`[uploadProjectToCloud] Project ${projectId} not found in local storage`);
            return false;
        }

        const nodes: CanvasNode[] = localData.nodes || [];
        const now = Date.now();
        const db = getFirestore(firebaseApp!);

        // What the cloud has, from the server itself. An answer from the cache can say "nothing"
        // while the cloud holds the workspace, filed and starred; with no answer, nothing is
        // written. The cloud's copy is left alone when it is the newer one.
        let existing: Record<string, any> | undefined;
        try {
            const snap = await getDocFromServer(doc(db, 'projects', projectId));
            existing = snap.exists() ? (snap.data() as Record<string, any>) : undefined;
        } catch (e: any) {
            console.warn(`[uploadProjectToCloud] Could not read the cloud's copy of ${projectId}; not uploading:`, e?.code || e?.message || e);
            return false;
        }
        if (existing && (existing.lastSaved || 0) > (localData.lastSaved || 0)) {
            console.warn(`[uploadProjectToCloud] The cloud's copy of "${localData.name}" is newer than this device's; not uploading`);
            return false;
        }
        // Whose the workspace is, where it is filed, whether it is starred and when it was made are
        // the cloud's to say once it has the workspace: this device's copy may never have known
        // (it was filed from another device, or before this copy was made), and writing its
        // blanks over the cloud's record unfiled whole libraries (2026-10-12).
        const organisation = existing
            ? {}
            : { ownerId: userId, createdAt: localData.createdAt ?? now, folderId: localData.folderId ?? null, isFavorite: !!localData.isFavorite };

        // Step 1: Write project metadata doc (no inline nodes array)
        // Sanitize all values so Firestore doesn't reject undefined/base64/blob
        const projectMeta = {
            ...sanitizeForFirestore({
                name: localData.name || 'Untitled Project',
                connections: localData.connections || [],
                history: sanitizeHistoryForSave(localData.history || []),
                transform: localData.transform || { x: 0, y: 0, scale: 1 },
                lastSaved: now,
                schemaVersion: 2 as ProjectSchemaVersion,
                nodeCount: nodes.length,
                ...organisation,
            }),
            // Thumbnail is a small data URL that sanitizeForFirestore would strip
            ...(typeof localData.thumbnail === 'string' && localData.thumbnail.startsWith('data:image/') ? { thumbnail: localData.thumbnail } : {}),
        };

        await setDoc(doc(db, 'projects', projectId), projectMeta, { merge: true });
        console.log(`[uploadProjectToCloud] ✅ Metadata written for "${localData.name}" (${nodes.length} nodes to upload)`);

        // Step 2: Write nodes in batches of 400 (Firestore limit is 500 ops/batch)
        const BATCH_SIZE = 400;
        let nodesWritten = 0;
        for (let i = 0; i < nodes.length; i += BATCH_SIZE) {
            const chunk = nodes.slice(i, i + BATCH_SIZE);
            const batch = writeBatch(db);
            for (const node of chunk) {
                const sanitizedNode = sanitizeForFirestore({ ...node, lastModifiedAt: now });
                batch.set(doc(db, 'projects', projectId, 'nodes', node.id), sanitizedNode);
            }
            await batch.commit();
            nodesWritten += chunk.length;
            console.log(`[uploadProjectToCloud] Wrote nodes ${i + 1}–${nodesWritten} of ${nodes.length}`);
        }

        markFirestoreWriteOk();
        console.log(`[uploadProjectToCloud] ✅ Project "${localData.name}" (${projectId}) fully uploaded (${nodes.length} nodes)`);
        return true;
    } catch (e: any) {
        console.error(`[uploadProjectToCloud] ❌ Failed:`, e.code, e.message, e);
        return false;
    }
};

/**
 * Sync ALL local-only projects to cloud (Firestore v2 format).
 * Checks which local projects are missing from Firestore and uploads them.
 * Called on app initialization to ensure cross-device consistency.
 */
export const syncAllLocalProjectsToCloud = async (userId: string): Promise<{ synced: number; failed: number; skipped: number }> => {
    const result = { synced: 0, failed: 0, skipped: 0 };
    
    if (!isFirebaseActive()) {
        console.warn('[syncAllLocal] Firebase not active, skipping sync');
        return result;
    }
    
    const auth = getAuth(firebaseApp!);
    if (!auth.currentUser) {
        console.warn('[syncAllLocal] No authenticated user, skipping sync');
        return result;
    }
    
    try {
        // 1. Get all local project IDs
        const localProjects = await listLocalProjects(userId);
        console.log(`[syncAllLocal] Found ${localProjects.length} local projects`);
        
        if (localProjects.length === 0) return result;
        
        // 2. Get all cloud project IDs (owned + shared — so shared projects aren't re-uploaded),
        // from the server itself. An answer from the cache (the connection dropped, the page has
        // only just loaded) can be empty while the cloud is full, and every workspace on this
        // device would then be written over the cloud's copy: no answer is better than that one.
        const db = getFirestore(firebaseApp!);
        let cloudProjectIds: Set<string>;
        try {
            const owned = await getDocsFromServer(query(collection(db, 'projects'), where('ownerId', '==', userId)));
            cloudProjectIds = new Set(owned.docs.map(d => d.id));
            const shared = await getDocsFromServer(query(collection(db, 'projects'), where('sharedUserIds', 'array-contains', userId)));
            shared.docs.forEach(d => cloudProjectIds.add(d.id));
        } catch (e: any) {
            console.warn('[syncAllLocal] Could not read the cloud\'s workspaces; nothing is uploaded until it answers:', e?.code || e?.message || e);
            result.skipped = localProjects.length;
            return result;
        }
        console.log(`[syncAllLocal] Found ${cloudProjectIds.size} cloud projects (owned + shared)`);
        
        // 3. Find local projects missing from cloud
        const missingFromCloud = localProjects.filter(p => !cloudProjectIds.has(p.id));
        console.log(`[syncAllLocal] ${missingFromCloud.length} local projects missing from cloud`);
        
        if (missingFromCloud.length === 0) {
            console.log('[syncAllLocal] All local projects already in cloud ✅');
            return result;
        }
        
        // 4. Upload each missing project
        for (const project of missingFromCloud) {
            try {
                console.log(`[syncAllLocal] Uploading "${project.name}" (${project.id})...`);
                const success = await uploadProjectToCloud(project.id, userId);
                if (success) {
                    result.synced++;
                    console.log(`[syncAllLocal] ✅ Synced "${project.name}"`);
                } else {
                    result.failed++;
                    console.warn(`[syncAllLocal] ❌ Failed to sync "${project.name}"`);
                }
            } catch (e) {
                result.failed++;
                console.error(`[syncAllLocal] ❌ Error syncing "${project.name}":`, e);
            }
        }
        
        console.log(`[syncAllLocal] Sync complete: ${result.synced} synced, ${result.failed} failed, ${result.skipped} skipped`);
        return result;
    } catch (e) {
        console.error('[syncAllLocal] Sync failed:', e);
        return result;
    }
};

// Legacy Helper
export const migrateLegacyProject = async (userId: string): Promise<string | null> => {
    const legacyKey = `user_project_${userId}`;
    const db = await openDB();
    
    const legacyData: any = await new Promise((resolve) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const req = store.get(legacyKey);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
    });

    if (legacyData) {
        const newId = await createProject(userId, "Migrated Workspace");
        await saveProject(
            legacyData.nodes || [],
            legacyData.connections || [],
            legacyData.history || [],
            legacyData.transform || {x:0, y:0, scale:1},
            userId,
            newId,
            "Migrated Workspace"
        );
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        transaction.objectStore(STORE_NAME).delete(legacyKey);
        return newId;
    }
    return null;
};

// ============================================
// LOCAL WORKSPACES (NO USER ID REQUIRED)
// For free users and non-signed-in users
// ============================================

export interface LocalWorkspace {
    id: string;
    name: string;
    ownerId?: string; // userId — scopes workspace to a specific user
    nodes: any[];
    connections: any[];
    history: any[];
    transform: { x: number; y: number; scale: number };
    createdAt: number;
    updatedAt: number;
    localFileMap?: Record<string, string>; // For offline image resolution
    thumbnail?: string; // Base64 encoded thumbnail image
    folderId?: string | null; // Project/folder container; null = unfiled
    isFavorite?: boolean;
}

export interface LocalWorkspaceMetadata {
    id: string;
    name: string;
    createdAt: number;
    updatedAt: number;
    nodeCount: number;
    thumbnail?: string; // Base64 encoded thumbnail image
    folderId?: string | null;
    isFavorite?: boolean;
    /** What its generations cost, in US dollars: its History's total */
    totalCost?: number;
}

const generateWorkspaceId = () => Math.random().toString(36).substr(2, 9);

/**
 * Create a new local workspace (scoped to ownerId when provided)
 */
export const createLocalWorkspace = async (name: string = 'Untitled', ownerId?: string, folderId: string | null = null): Promise<LocalWorkspace> => {
    const db = await openDB();
    const workspace: LocalWorkspace = {
        id: generateWorkspaceId(),
        name,
        ownerId: ownerId || undefined,
        nodes: [],
        connections: [],
        history: [],
        transform: { x: 0, y: 0, scale: 1 },
        createdAt: Date.now(),
        updatedAt: Date.now(),
        folderId,
    };
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([LOCAL_WORKSPACES_STORE], 'readwrite');
        const store = transaction.objectStore(LOCAL_WORKSPACES_STORE);
        const request = store.add(workspace);
        request.onsuccess = () => resolve(workspace);
        request.onerror = () => reject(request.error);
    });
};

/**
 * Save/update a local workspace
 */
export const saveLocalWorkspace = async (
    workspaceId: string,
    data: {
        name?: string;
        nodes: any[];
        connections: any[];
        history: any[];
        transform: { x: number; y: number; scale: number };
        localFileMap?: Record<string, string>;
        thumbnail?: string;
        ownerId?: string;
    }
): Promise<void> => {
    const db = await openDB();

    // First get existing workspace to preserve metadata
    const existing = await loadLocalWorkspace(workspaceId);

    const finalThumbnail = data.thumbnail || existing?.thumbnail;
    // A local address a view left in the data is saved as the file reference it stands for
    if (holdsLocalAddress(data.nodes) || holdsLocalAddress(data.history)) await loadLocalAddressLookup();

    const workspace: LocalWorkspace = {
        id: workspaceId,
        name: data.name || existing?.name || 'Untitled',
        ownerId: data.ownerId || existing?.ownerId || undefined,
        nodes: withStoredReferences(data.nodes, 'drop'),
        connections: data.connections,
        history: withStoredReferences(data.history, 'drop'),
        transform: data.transform,
        createdAt: existing?.createdAt || Date.now(),
        updatedAt: Date.now(),
        localFileMap: data.localFileMap,
        thumbnail: finalThumbnail,
        // Organisation metadata is owned by the library page, so a canvas save keeps it.
        folderId: existing?.folderId ?? null,
        isFavorite: !!existing?.isFavorite,
    };
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([LOCAL_WORKSPACES_STORE], 'readwrite');
        const store = transaction.objectStore(LOCAL_WORKSPACES_STORE);
        const request = store.put(workspace);
        request.onsuccess = () => { noteWorkspaceSaved(workspaceId, workspace.updatedAt); resolve(); };
        request.onerror = () => reject(request.error);
    });
};

/**
 * Load a local workspace by ID
 */
export const loadLocalWorkspace = async (workspaceId: string): Promise<LocalWorkspace | null> => {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([LOCAL_WORKSPACES_STORE], 'readonly');
        const store = transaction.objectStore(LOCAL_WORKSPACES_STORE);
        const request = store.get(workspaceId);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
    });
};

/**
 * List all local workspaces (metadata + thumbnail for performance)
 * Includes thumbnail directly to avoid loading full workspace data
 * When ownerId is provided, only returns workspaces belonging to that user
 */
export const listLocalWorkspaces = async (ownerId?: string): Promise<LocalWorkspaceMetadata[]> => {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([LOCAL_WORKSPACES_STORE], 'readonly');
        const store = transaction.objectStore(LOCAL_WORKSPACES_STORE);
        const workspaces: LocalWorkspaceMetadata[] = [];
        
        const request = store.openCursor();
        request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result;
            if (cursor) {
                const ws = cursor.value as LocalWorkspace;
                // Filter by ownerId: show workspace if it belongs to the user,
                // or if no ownerId filter is given, or if workspace has no owner (legacy)
                const belongsToUser = !ownerId || !ws.ownerId || ws.ownerId === ownerId;
                if (belongsToUser) {
                    workspaces.push({
                        id: ws.id,
                        name: ws.name,
                        createdAt: ws.createdAt,
                        updatedAt: ws.updatedAt,
                        nodeCount: ws.nodes?.length || 0,
                        thumbnail: ws.thumbnail, // Include thumbnail directly
                        folderId: ws.folderId ?? null,
                        isFavorite: !!ws.isFavorite,
                        totalCost: historyTotalCost(Array.isArray(ws.history) ? ws.history : []),
                    });
                }
                cursor.continue();
            } else {
                // Sort by updatedAt descending (most recent first)
                workspaces.sort((a, b) => b.updatedAt - a.updatedAt);
                resolve(workspaces);
            }
        };
        request.onerror = () => reject(request.error);
    });
};

/**
 * Delete a local workspace
 */
export const deleteLocalWorkspace = async (workspaceId: string): Promise<void> => {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([LOCAL_WORKSPACES_STORE], 'readwrite');
        const store = transaction.objectStore(LOCAL_WORKSPACES_STORE);
        const request = store.delete(workspaceId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

/**
 * Rename a local workspace
 */
export const renameLocalWorkspace = async (workspaceId: string, newName: string): Promise<void> => {
    const workspace = await loadLocalWorkspace(workspaceId);
    if (!workspace) throw new Error('Workspace not found');
    
    workspace.name = newName;
    workspace.updatedAt = Date.now();
    
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([LOCAL_WORKSPACES_STORE], 'readwrite');
        const store = transaction.objectStore(LOCAL_WORKSPACES_STORE);
        const request = store.put(workspace);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

/**
 * Duplicate a local workspace
 */
export const duplicateLocalWorkspace = async (workspaceId: string, newName?: string): Promise<LocalWorkspace> => {
    const original = await loadLocalWorkspace(workspaceId);
    if (!original) throw new Error('Workspace not found');
    
    const duplicate: LocalWorkspace = {
        ...original,
        id: generateWorkspaceId(),
        name: newName || `${original.name} (Copy)`,
        createdAt: Date.now(),
        updatedAt: Date.now(),
    };
    
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([LOCAL_WORKSPACES_STORE], 'readwrite');
        const store = transaction.objectStore(LOCAL_WORKSPACES_STORE);
        const request = store.add(duplicate);
        request.onsuccess = () => resolve(duplicate);
        request.onerror = () => reject(request.error);
    });
};

/**
 * Check if any local workspaces exist (optionally scoped to ownerId)
 */
export const hasLocalWorkspaces = async (ownerId?: string): Promise<boolean> => {
    const workspaces = await listLocalWorkspaces(ownerId);
    return workspaces.length > 0;
};

/**
 * Get or create default local workspace
 * Used for non-signed-in users on first visit
 */
export const getOrCreateDefaultLocalWorkspace = async (ownerId?: string): Promise<LocalWorkspace> => {
    const workspaces = await listLocalWorkspaces(ownerId);
    
    if (workspaces.length > 0) {
        // Return the most recently updated workspace
        const latest = await loadLocalWorkspace(workspaces[0].id);
        if (latest) return latest;
    }
    
    // Create a new default workspace
    const defaultWorkspace: LocalWorkspace = {
        id: generateWorkspaceId(),
        name: 'My First Workspace',
        ownerId: ownerId || undefined,
        nodes: [],
        connections: [],
        history: [],
        transform: { x: 0, y: 0, scale: 1 },
        createdAt: Date.now(),
        updatedAt: Date.now(),
    };
    
    await saveLocalWorkspace(defaultWorkspace.id, {
        name: defaultWorkspace.name,
        nodes: defaultWorkspace.nodes,
        connections: defaultWorkspace.connections,
        history: defaultWorkspace.history,
        transform: defaultWorkspace.transform,
        ownerId: ownerId || undefined,
    });
    return defaultWorkspace;
};

// --- RECENT FILES MANAGEMENT ---

/**
 * Get recent files from all sources (cloud + local)
 */
export const getRecentFiles = async (limit: number = 12, ownerId?: string): Promise<RecentFileMetadata[]> => {
    const allFiles: RecentFileMetadata[] = [];
    
    // Get cloud projects if user is signed in
    try {
        // Note: We can't import getCurrentUser here due to circular deps, so we skip cloud projects for now
        // This function will be called from App.tsx where user context is available
        // const user = getCurrentUser();
        // if (user && isFirebaseActive()) {
        //     const projects = await listProjects(user.sub, true);
        //     const cloudFiles: RecentFileMetadata[] = projects.map((p: ProjectMetadata) => ({
        //         id: p.id,
        //         name: p.name,
        //         lastSaved: p.lastSaved,
        //         lastOpened: p.lastOpened || p.lastSaved,
        //         ownerId: p.ownerId,
        //         isLocal: false,
        //         isFavorite: false, // TODO: Add favorite tracking
        //     }));
        //     allFiles.push(...cloudFiles);
        // }
    } catch (error) {
        console.warn('[getRecentFiles] Failed to load cloud projects:', error);
    }
    
    // Get local workspaces - thumbnail is now included in metadata for performance
    try {
        const localWorkspaceMetadata = await listLocalWorkspaces(ownerId);
        const localFiles: RecentFileMetadata[] = localWorkspaceMetadata.map(w => ({
            id: w.id,
            name: w.name,
            lastSaved: w.updatedAt,
            lastOpened: w.updatedAt,
            createdAt: w.createdAt,
            nodeCount: w.nodeCount,
            thumbnail: w.thumbnail, // Thumbnail is now in metadata, no need to load full workspace
            isLocal: true,
            isFavorite: !!w.isFavorite,
            folderId: w.folderId ?? null,
        }));
        allFiles.push(...localFiles);
    } catch (error) {
        console.warn('[getRecentFiles] Failed to load local workspaces:', error);
    }
    
    // Sort by last opened/saved and limit
    allFiles.sort((a, b) => (b.lastOpened || b.lastSaved) - (a.lastOpened || a.lastSaved));
    return allFiles.slice(0, limit);
};

/**
 * Record that a workspace was opened on this device.
 *
 * "Last opened" is per-device and lives in the IndexedDB `recentFiles` store as
 * `{ id, lastOpened }`, for cloud, local and shared workspaces alike. Opening a
 * workspace no longer rewrites its record (which used to bump `updatedAt` and
 * make an untouched workspace look freshly edited). An optional thumbnail is
 * still applied to local-store workspaces without touching their timestamps.
 */
export const updateLastOpened = async (workspaceId: string, isLocal: boolean = false, thumbnail?: string, ownerId?: string): Promise<void> => {
    if (!workspaceId) return;
    try {
        const db = await openDB();
        await new Promise<void>((resolve, reject) => {
            const store = db.transaction([RECENT_FILES_STORE], 'readwrite').objectStore(RECENT_FILES_STORE);
            const request = store.put({ id: workspaceId, lastOpened: Date.now(), ...(ownerId ? { ownerId } : {}) });
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.warn('[updateLastOpened] Failed to record last opened:', error);
    }

    if (isLocal && thumbnail) {
        try {
            await patchIndexedDbRecord(LOCAL_WORKSPACES_STORE, workspaceId, { thumbnail }, false);
        } catch (error) {
            console.warn('[updateLastOpened] Failed to store thumbnail:', error);
        }
    }
};

/**
 * workspaceId → last opened timestamp on this device (see updateLastOpened).
 * When `ownerId` is given, only that account's records are returned so one
 * person's Recent view never shows what another account opened on the device.
 */
export const getLastOpenedMap = async (ownerId?: string): Promise<Map<string, number>> => {
    const map = new Map<string, number>();
    try {
        const db = await openDB();
        const rows: any[] = await new Promise((resolve, reject) => {
            const request = db.transaction([RECENT_FILES_STORE], 'readonly').objectStore(RECENT_FILES_STORE).getAll();
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });
        for (const row of rows) {
            if (!row || typeof row.id !== 'string' || typeof row.lastOpened !== 'number') continue;
            if (ownerId && row.ownerId !== ownerId) continue;
            map.set(row.id, row.lastOpened);
        }
    } catch (error) {
        console.warn('[getLastOpenedMap] Failed to read recent files:', error);
    }
    return map;
};

/** Forget a workspace's last-opened record (call after deleting it). */
export const clearLastOpened = async (workspaceId: string): Promise<void> => {
    try {
        const db = await openDB();
        await new Promise<void>((resolve, reject) => {
            const request = db.transaction([RECENT_FILES_STORE], 'readwrite').objectStore(RECENT_FILES_STORE).delete(workspaceId);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    } catch {
        /* non-fatal */
    }
};


// --- CLOUD WRITE GUARDS ---

export const OFFLINE_MESSAGE = 'You appear to be offline. Check your connection and try again.';
const CLOUD_TIMEOUT_MS = 10_000;

export const isOffline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * The Firestore web SDK (without persistence) keeps a write pending forever while
 * offline; race it against a timeout so callers can fail fast and tell the user.
 * Takes a thunk so nothing is queued in the SDK when we already know we are offline.
 */
export const withCloudTimeout = <T,>(run: () => Promise<T>, ms: number = CLOUD_TIMEOUT_MS): Promise<T> => {
    if (isOffline()) return Promise.reject(new Error(OFFLINE_MESSAGE));
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new Error('The server did not respond in time. The change may still apply once the connection recovers; refresh to check.')),
            ms,
        );
        run().then(
            (value) => { clearTimeout(timer); resolve(value); },
            (err) => { clearTimeout(timer); reject(err); },
        );
    });
};

/**
 * User-facing message for a failed Firestore write. Keeps the SDK error code so a
 * developer can tell "rules not deployed" (permission-denied) from a flaky network.
 */
export const describeCloudError = (e: unknown, fallback: string = 'Could not save changes'): string => {
    if (e instanceof Error && e.message === OFFLINE_MESSAGE) return OFFLINE_MESSAGE;
    const code = (e as { code?: unknown } | null)?.code;
    if (typeof code === 'string' && code) {
        const short = code.replace(/^firestore\//, '');
        if (short === 'permission-denied') {
            return `${fallback}: the server refused the write (permission-denied). If you are the site owner, check that the latest Firestore rules are deployed.`;
        }
        return `${fallback} (${short}). Please try again.`;
    }
    if (e instanceof Error && /did not respond in time/.test(e.message)) return e.message;
    return `${fallback}. Please try again.`;
};

// --- WORKSPACE ORGANISATION METADATA (folder, star, name) ---

export interface WorkspaceMetaPatch {
    folderId?: string | null;
    isFavorite?: boolean;
    name?: string;
    /** Stamp legacy ownerless local workspaces with their user when they are organised. */
    ownerId?: string;
}

/** Read-modify-write a record in an IndexedDB store (keyPath or out-of-line key). */
const patchIndexedDbRecord = async (
    storeName: string,
    key: string,
    patch: Record<string, any>,
    outOfLineKey: boolean,
): Promise<boolean> => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const getRequest = store.get(key);
        getRequest.onsuccess = () => {
            const existing = getRequest.result;
            if (!existing) { resolve(false); return; }
            const record = { ...existing, ...patch };
            const request = outOfLineKey ? store.put(record, key) : store.put(record);
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error);
        };
        getRequest.onerror = () => reject(getRequest.error);
    });
};

/**
 * Patch a workspace's organisation metadata (folder placement, star, name)
 * without touching its canvas contents.
 *
 * - Local-store workspaces (`isLocalStore`) live only in IndexedDB `localWorkspaces`.
 * - Cloud workspaces are patched in Firestore (merge) when cloud sync is on, and the
 *   IndexedDB `projects` mirror is patched too so list merging never regresses.
 */
export const updateWorkspaceMeta = async (
    workspaceId: string,
    isLocalStore: boolean,
    patch: WorkspaceMetaPatch,
    cloudSyncEnabled: boolean = false,
): Promise<void> => {
    validateParams({ workspaceId }, 'updateWorkspaceMeta');
    const cleanPatch = removeUndefined(patch) as Record<string, any>;
    if (Object.keys(cleanPatch).length === 0) return;
    logger.debug('Storage', `updateWorkspaceMeta | ${workspaceId} | local: ${isLocalStore} | ${JSON.stringify(cleanPatch)}`);

    if (isLocalStore) {
        const ok = await patchIndexedDbRecord(LOCAL_WORKSPACES_STORE, workspaceId, cleanPatch, false);
        if (!ok) throw new Error('Workspace not found');
        return;
    }

    // ownerId never changes on cloud rows (rules forbid it) — it is only for local stamping.
    const { ownerId: _ignored, ...cloudPatch } = cleanPatch;
    let cloudOk = false;
    if (isFirebaseActive() && cloudSyncEnabled && Object.keys(cloudPatch).length > 0) {
        try {
            const db = getFirestore(firebaseApp!);
            // updateDoc (not setDoc/merge) so a workspace that only exists locally is not
            // half-created in the cloud; 'not-found' falls back to the local mirror.
            await withCloudTimeout(() => updateDoc(doc(db, 'projects', workspaceId), cloudPatch));
            cloudOk = true;
        } catch (e: any) {
            if (e?.code === 'not-found') {
                logger.debug('Storage', `updateWorkspaceMeta: ${workspaceId} is not in the cloud yet, patching mirror only`);
            } else {
                console.error('[updateWorkspaceMeta] Cloud patch failed:', e);
                throw new Error(describeCloudError(e));
            }
        }
    }
    const localOk = await patchIndexedDbRecord(STORE_NAME, workspaceId, cleanPatch, true);
    if (!cloudOk && !localOk) {
        throw new Error('Workspace not found');
    }
};

/**
 * Toggle favorite status for a workspace.
 * Kept for backwards compatibility; delegates to updateWorkspaceMeta.
 */
export const toggleFavorite = async (
    workspaceId: string,
    favorite: boolean,
    isLocalStore: boolean = false,
    cloudSyncEnabled: boolean = false,
): Promise<void> => {
    try {
        await updateWorkspaceMeta(workspaceId, isLocalStore, { isFavorite: favorite }, cloudSyncEnabled);
    } catch (error) {
        console.warn('[toggleFavorite] Failed to update favorite status:', error);
    }
};

// ==================== WORKSPACE SHARING ====================

/**
 * Get the Firebase Auth token for API calls.
 */
const getAuthToken = async (): Promise<string | null> => {
    if (!isFirebaseActive()) return null;
    const auth = getAuth(firebaseApp!);
    if (!auth.currentUser) return null;
    try {
        return await auth.currentUser.getIdToken();
    } catch {
        return null;
    }
};

const getApiBase = (): string => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.origin;
    }
    return '';
};

/**
 * Share a workspace with a user by email via the sharing API.
 */
export const shareProject = async (
    projectId: string,
    email: string,
    permission: 'view' | 'edit',
): Promise<{ success: boolean; type?: string; message?: string; error?: string }> => {
    const token = await getAuthToken();
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
        const res = await fetch(`${getApiBase()}/api/sharing`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ projectId, email, permission }),
        });
        const data = await res.json();
        if (!res.ok) {
            console.error('[shareProject] API error:', res.status, JSON.stringify(data, null, 2));
        }
        return data;
    } catch (e: any) {
        return { success: false, error: e.message };
    }
};

/**
 * Revoke a user's access to a shared workspace.
 */
export const unshareProject = async (
    projectId: string,
    opts: { userId?: string; email?: string; invitationId?: string },
): Promise<{ success: boolean; error?: string }> => {
    const token = await getAuthToken();
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
        const res = await fetch(`${getApiBase()}/api/sharing`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ projectId, ...opts }),
        });
        return await res.json();
    } catch (e: any) {
        return { success: false, error: e.message };
    }
};

/**
 * Update a shared user's permission level.
 */
export const updateSharePermission = async (
    projectId: string,
    userId: string,
    permission: 'view' | 'edit',
): Promise<{ success: boolean; error?: string }> => {
    const token = await getAuthToken();
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
        const res = await fetch(`${getApiBase()}/api/sharing`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ projectId, userId, permission }),
        });
        return await res.json();
    } catch (e: any) {
        return { success: false, error: e.message };
    }
};

/**
 * List shared users and pending invitations for a project.
 */
export const listSharedUsers = async (
    projectId: string,
): Promise<{ owner?: any; users?: any[]; pendingInvitations?: any[]; error?: string }> => {
    const token = await getAuthToken();
    if (!token) return { error: 'Not authenticated' };

    try {
        const res = await fetch(`${getApiBase()}/api/sharing/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return await res.json();
    } catch (e: any) {
        return { error: e.message };
    }
};

// ── Library projects shared with other people (netlify/functions/api/project-sharing.ts) ──

const projectSharingCall = async <T,>(method: string, path: string, body?: unknown): Promise<T & { success?: boolean; error?: string }> => {
    const token = await getAuthToken();
    if (!token) return { success: false, error: 'Not authenticated' } as T & { success: boolean; error: string };
    try {
        const res = await fetch(`${getApiBase()}/api/project-sharing${path}`, {
            method,
            headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
            ...(body ? { body: JSON.stringify(body) } : {}),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok && !data.error) data.error = `The sharing service answered ${res.status}`;
        return data;
    } catch (e: any) {
        return { success: false, error: e.message } as T & { success: boolean; error: string };
    }
};

/** Share a library project (and everything in it) with someone by email */
export const shareLibraryProject = (projectId: string, email: string, permission: 'view' | 'edit') =>
    projectSharingCall<{ type?: string; message?: string }>('POST', '', { projectId, email, permission });

/** Take someone off a library project, leave it (userId = yourself), or cancel an invitation */
export const unshareLibraryProject = (projectId: string, opts: { userId?: string; invitationId?: string }) =>
    projectSharingCall<{ message?: string }>('DELETE', '', { projectId, ...opts });

export const updateLibraryProjectPermission = (projectId: string, userId: string, permission: 'view' | 'edit') =>
    projectSharingCall<{ message?: string }>('PATCH', '', { projectId, userId, permission });

export const listLibraryProjectMembers = (projectId: string) =>
    projectSharingCall<{ owner?: any; users?: any[]; pendingInvitations?: any[] }>('GET', `/${encodeURIComponent(projectId)}`);

/** A project someone wants to share with the signed-in person, waiting for their answer */
export interface ProjectInvitation {
    id: string;
    projectId: string;
    projectName: string;
    permission: 'view' | 'edit';
    inviterName: string;
    createdAt: number;
}

/** The signed-in person's project invitations that wait for an answer (empty when they cannot be read) */
export const listMyProjectInvitations = async (): Promise<ProjectInvitation[]> => {
    const result = await projectSharingCall<{ invitations?: ProjectInvitation[] }>('GET', '/invitations');
    return Array.isArray(result.invitations) ? result.invitations : [];
};

/** Accept (the project is shared with the person) or decline an invitation to a project */
export const answerProjectInvitation = (invitationId: string, accept: boolean) =>
    projectSharingCall<{ projectId?: string; projectName?: string }>('POST', accept ? '/accept' : '/decline', { invitationId });

/** The owner's app, after it moved or made something in a shared project: its people get it, or lose it */
export const syncLibraryProject = (projectId: string) =>
    projectSharingCall<{ workspaces?: number; folders?: number }>('POST', '/sync', { projectId });

/**
 * Resolve pending invitations for the current user (called on login).
 */
export const resolveInvitations = async (): Promise<{
    resolved: Array<{ projectId: string; projectName: string; permission: string; kind?: 'project' }>;
    count: number;
}> => {
    const token = await getAuthToken();
    if (!token) return { resolved: [], count: 0 };

    try {
        const res = await fetch(`${getApiBase()}/api/resolve-invitations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });
        const data = await res.json();
        return {
            resolved: Array.isArray(data?.resolved) ? data.resolved : [],
            count: typeof data?.count === 'number' ? data.count : 0,
        };
    } catch {
        return { resolved: [], count: 0 };
    }
};

/**
 * Write a changelog entry to Firestore for a project.
 */
export const writeChangelogEntry = async (
    projectId: string,
    entry: {
        userId: string;
        userEmail: string;
        userName: string;
        action: string;
        nodeId?: string;
        nodeType?: string;
        details?: string;
    },
): Promise<void> => {
    if (!isFirebaseActive()) return;
    try {
        const db = getFirestore(firebaseApp!);
        const changelogRef = collection(db, 'projects', projectId, 'changelog');
        const docRef = doc(changelogRef);
        await setDoc(docRef, {
            ...entry,
            timestamp: Date.now(),
        });
    } catch (e) {
        console.warn('[writeChangelogEntry] Failed:', e);
    }
};

/**
 * Fetch changelog entries for a project (paginated).
 */
export const fetchChangelog = async (
    projectId: string,
    opts?: { limit?: number; before?: number },
): Promise<{ entries: any[]; hasMore: boolean; nextCursor?: number }> => {
    const token = await getAuthToken();
    if (!token) return { entries: [], hasMore: false };

    try {
        const params = new URLSearchParams();
        if (opts?.limit) params.set('limit', String(opts.limit));
        if (opts?.before) params.set('before', String(opts.before));

        const res = await fetch(
            `${getApiBase()}/api/changelog/${projectId}?${params.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } },
        );
        return await res.json();
    } catch {
        return { entries: [], hasMore: false };
    }
};
